// i18n — TR kaynak + EN kaplama (overlay).
// ─────────────────────────────────────────────────────────────────────────────
// Panelin kaynak dili TÜRKÇE'dir (PLAN.md kararı): tüm JSX metinleri Türkçe
// yazılır, sözlük gerektirmez ve tasarım dosyasıyla birebir karşılaştırılabilir.
// EN'e geçilince render sonrası DOM'daki metin düğümleri sözlükten çevrilir.
//
// NEDEN t() değil: tasarım (Call Agent Admin.dc.html) bu modeli kullanıyor ve
// EN sözlüğü tam. t() sarmalayıcısı her dizgiyi dolaylı hale getirip tasarımla
// görsel diff'i imkânsız kılardı. Bedeli: çeviri render'dan SONRA uygulanır.
//
// İki kural bunu güvenli kılar:
//   1. Kök <div key={'lang-'+lang}> — dil değişince ağaç yeniden monte edilir,
//      yani DOM taze TR'ye döner, sonra (EN ise) yeniden çevrilir.
//   2. useLayoutEffect (bağımlılık dizisi YOK) — her commit'ten sonra, boyamadan
//      ÖNCE çalışır; React bir düğümü yeniden yazsa bile çeviri geri gelir ve
//      kullanıcı asla Türkçe'nin "parlamasını" görmez.
// State App'te tutulur (kök div'in ÜSTÜNDE), remount state'i sıfırlamaz.

import { useEffect, useLayoutEffect } from 'react';
import type { Lang } from './types';

const EN: Record<string, string> = {
  // giriş
  'Çağrı merkezi süpervizör paneli': 'Call center supervisor panel',
  'Giriş Yap': 'Sign In',
  'Devam etmek için hesabınıza giriş yapın.': 'Sign in to your account to continue.',
  'KULLANICI ADI': 'USERNAME',
  ŞİFRE: 'PASSWORD',
  'kullanıcı adı': 'username',
  'Kullanıcı adı veya şifre hatalı.': 'Incorrect username or password.',
  'Bağlantı hatası. Lütfen tekrar deneyin.': 'Connection error. Please try again.',
  'Giriş yapılıyor…': 'Signing in…',
  'DEMO HESAPLAR': 'DEMO ACCOUNTS',
  'Yönetici · tüm markalar · Ayarlar dahil': 'Administrator · all brands · incl. Settings',
  'Süpervizör · Vegas + Prime': 'Supervisor · Vegas + Prime',

  // kabuk / topbar
  'Sistem aktif': 'System active',
  'Menüyü daralt': 'Collapse menu', // title=""
  'Menüyü genişlet': 'Expand menu',
  'Açık tema': 'Light theme', // title=""
  'Koyu tema': 'Dark theme', // title=""
  'Aktif marka:': 'Active brand:',
  Tümü: 'All',
  Yok: 'None',
  Çıkış: 'Log out',
  Yönetici: 'Administrator',
  Süpervizör: 'Supervisor',
  'Yönetici · Tüm markalar': 'Administrator · All brands',

  // nav
  'Canlı Durum': 'Live Status',
  Raporlar: 'Reports',
  'Temsilci Detayı': 'Agent Detail',
  Molalar: 'Breaks',
  Uyarılar: 'Alerts',
  Ayarlar: 'Settings',
  'Temsilcilerin anlık arama durumları ve canlı akış': 'Real-time agent call status and live feed',
  'Marka ve departman bazlı performans raporları': 'Brand and department performance reports',
  'Tekil temsilci metrikleri ve arama geçmişi': 'Individual agent metrics and call history',
  'Mola takibi, süreler ve kurallar': 'Break tracking, durations and rules',
  'Eşik aşımları ve sistem uyarıları': 'Threshold breaches and system alerts',
  'DARK izleme modülü': 'DARK monitoring module',
  'Sistem, kullanıcı ve erişim ayarları': 'System, user and access settings',
  modülü: 'module',
  '. Bu ekran bir sonraki adımda hazırlanacak.': '. This screen will be prepared in the next step.',
  Yakında: 'Coming soon',

  // canlı durum
  CANLI: 'LIVE',
  'Son güncelleme': 'Last updated',
  Yenile: 'Refresh', // title="" — translateDOM [title] özniteliklerini de çevirir
  Çalışıyor: 'Working',
  Molada: 'On break',
  Çevrimdışı: 'Offline',
  'Bugün toplam çağrı': 'Total calls today',
  // "Bu hafta" şeridi (görev #195/#196) — Haftalık Rapor'un cari haftası; değer satırları sayı taşır,
  // etiketler AYRI düğüm. 'KRİTİK HATA' / 'TOPLAM ÇAĞRI' / 'MARKA' / 'Cevaplama:' zaten aşağıda.
  'Bu hafta': 'This week',
  "Pazartesi 00:00'dan beri": 'Since Monday 00:00',
  'GÜNCEL YATIRIM (24S)': 'CURRENT DEPOSIT (24H)',
  'Pencereleri henüz kapanmamış görüşmeler eklendikçe artar': 'Grows as the 24h/48h windows of recent conversations close',
  'TOPLAM SMS': 'TOTAL SMS',
  'QMS ORTALAMASI': 'QMS AVERAGE',
  'TOPLAM KRİTİK HATA': 'TOTAL CRITICAL MISTAKES',
  'BAŞARI ORT. (24S)': 'SUCCESS RATE AVG. (24H)',
  '48s:': '48h:',
  'Başarısız:': 'Failed:',
  'Değerlendirme:': 'Evaluations:',
  'Bu hafta okunamadı': 'This week could not be read',
  'Bu haftanın toplamları okunamadı.': "This week's totals could not be read.",
  'Bu hafta hesaplanıyor…': 'Computing this week…',
  'Yeni uyarı': 'New alerts',
  'Tüm departmanlar': 'All departments',
  'Departman yok': 'No department', // Ayarlar → departman <select>'inde "atanmamış" seçeneği
  'Aktivite yok': 'No activity', // kart alt satırı: hiç görülmemiş temsilci ("Son aktivite:" düşer)
  'Temsilci ara…': 'Search agent…',
  temsilci: 'agents', // "17 / 17 temsilci" sayacında " temsilci" AYRI bir düğüm — tam kalıp eşleşemez
  'Canlı veriler yükleniyor…': 'Loading live data…',
  'Temsilci bulunamadı': 'No agents found',
  'Üstteki marka filtresinden en az bir marka seçin.': 'Select at least one brand from the filter above.',
  'Seçili filtrelerle eşleşen temsilci bulunamadı.': 'No agents match the selected filters.',
  'BUGÜNKÜ ÇAĞRI': 'CALLS TODAY',
  'İlk–Son:': 'First–Last:',
  'Terminal:': 'Terminal:',
  // kart metrik şeridi başlıkları (TERMİNAL sözlükte ZATEN var — temsilci detayı bölümü)
  ÇAĞRI: 'CALLS',
  'İLK–SON': 'FIRST–LAST',
  'Son aktivite:': 'Last activity:',
  'Temsilci detayını aç': 'Open agent detail',
  'Süre aşıldı': 'Time exceeded',
  // Molalar: yöneticinin molayı zorla sonlandırma denetimi (PLAN req 5).
  'Molayı Sonlandır': 'End Break',
  'Kapatılıyor…': 'Closing…',
  Mola: 'Break',
  // Eklentinin AwaitingOutcome durumu: müşteri açık, sonuç henüz girilmemiş. Görüşme
  // VE not yazımını kapsar — bu yüzden "Görüşmede"/"On call" DEĞİL (kimse hatta yokken
  // de sürüyor). Süre ayrı bir düğümde (fmtElapsed → "4:12" / "1sa 20dk"), o yüzden
  // burada yalnızca durağan etiket var; sayı zaten i18n.ts'in mevcut regex'iyle çevriliyor.
  'Müşteri işlemde': 'Handling customer',
  // Aynı veri, HANDLING_IDLE_AFTER_MIN'i aşınca: müşteri açık ama kimse ilgilenmiyor.
  // EN KISA TUTULDU ("no activity" DEĞİL): alt satır sarmaz, kart `overflow:hidden` ile
  // KIRPAR. 300px'lik ızgara minimumunda ölçüldü — "Customer open, no activity" + "10h 59m"
  // yalnızca 4.4px pay bırakıyordu (chevron kırpılır); "idle" ile pay 41px.
  'Müşteri açık, işlem yok': 'Customer open, idle',

  // vardiya planı (Info sekmesi)
  İzinli: 'Day off',
  'Takım lideri': 'Team lead',
  'Sabah vardiyası': 'Morning shift',
  'Akşam vardiyası': 'Evening shift',
  // Gelen çağrı görevi (migration 015): santralden gelen çağrı, eklentiye uğramaz.
  'Sabah gelen çağrı': 'Morning inbound',
  'Akşam gelen çağrı': 'Evening inbound',
  'Vardiya planı': 'Shift roster',
  'Planlanan vardiya': 'Planned shift',
  // 'Plan dışı çalışma' kuralı EMEKLİ (2026-07-27) — ne kart çipi ne uyarı üretiliyor.
  // Sözlük satırları KALIYOR: RULE_TITLES başlığı duruyor ve migration 008'den önce
  // yazılmış bir satır EN modunda hâlâ doğru çevrilsin. Aşağıdaki (§ açıklama regex'i)
  // `^Planda … olmasına rağmen N çağrı yapıldı` için de aynısı geçerli.
  'Plan dışı çalışma': 'Off-plan work',
  'İzinli / TL': 'Day off / TL',
  'CALL AGENT': 'CALL AGENT',
  'CALL TEAM LEAD': 'CALL TEAM LEAD',

  // raporlar
  DÖNEM: 'PERIOD',
  KIRILIM: 'GRANULARITY',
  Bugün: 'Today',
  Dün: 'Yesterday',
  'Bu Hafta': 'This Week',
  'Geçen Hafta': 'Last Week',
  'Bu Ay': 'This Month',
  Günlük: 'Daily',
  Haftalık: 'Weekly',
  Aylık: 'Monthly',
  'Rapor hazırlanıyor…': 'Preparing report…',
  'Veri yok': 'No data',
  'Seçili dönem ve markalar için veri bulunamadı.': 'No data for the selected period and brands.',
  'Liste başına çağrı': 'Calls per list',
  'Seçili dönem · tüm temsilciler': 'Selected period · all agents',
  'Dönem trendi': 'Period trend',
  'Toplam çağrı ·': 'Total calls ·',
  'En yüksek': 'Highest',
  'Çağrı Sayıları': 'Call Counts',
  'Temsilci · liste × sonuç dağılımı': 'Agent · list × result distribution',
  TEMSİLCİ: 'AGENT',
  DEPARTMAN: 'DEPARTMENT',
  TOPLAM: 'TOTAL',
  CEVAPLAMA: 'ANSWER RATE',
  manuel: 'manual',
  'Bekleme Süreleri': 'Wait Times',
  'Çağrı sonrası bekleme analizi': 'Post-call wait analysis',
  'Tembellik metriği': 'Laziness metric',
  'Verimsiz sonuç sonrası ort. bekleme · eşik': 'Avg wait after unproductive result · threshold',
  'ORT. BEKLEME': 'AVG WAIT',
  BOŞLUK: 'GAPS',
  'Sonuç tipine göre bekleme': 'Wait by result type',
  'Her sonuç sonrası ort. bekleme (dk:sn)': 'Avg wait after each result (m:ss)',

  // haftalık rapor
  // DİKKAT: bu sayfadaki SHEET DEĞERLERİ (sekme adları ARAMA TALEPLERİ/YATIRIM/…,
  // kaynak kovaları SLACK/FORM/TAKİP/DİĞER, talep sebepleri HESAP KAPATMA/BONUS/…,
  // temsilci adları) KASITLI olarak sözlüğe GİRMEZ — olduğu gibi basılırlar.
  // Yalnız halka grafiğin kendi ürettiği "Diğer" kovası çevrilir (zaten mola
  // sebeplerinden sözlükte). Sonuç sütun başlıkları da burada YOK: DOM'daki metin
  // getStatusSystems().tr etiketinin AYNISI ('Yanıtladı'…) ve büyük harf CSS ile
  // veriliyor, böylece aşağıdaki mevcut durum satırları onları da çeviriyor.
  'Haftalık Rapor': 'Weekly Report',
  'Vegas + Prime haftalık çağrı ve yatırım karnesi': 'Weekly call and deposit scorecard for Vegas + Prime',
  HAFTA: 'WEEK',
  'Önceki hafta': 'Previous week', // title=""
  'Sonraki hafta': 'Next week', // title=""
  Kesinleşti: 'Final',
  // Caveats şeridinin başlığı. Satırların KENDİSİ sunucudan gelir ve sayı/tarih taşır → sözlüğe
  // giremez; onları translatePhrase'deki "Haftalık Rapor Veri uyarısı" regex'leri çevirir.
  'Veri uyarısı': 'Data warning',
  'Geçici veri · kesinleşme:': 'Provisional · final after:',
  'Bu haftanın bazı satırlarında eski tarih biçimi var; sayılar biraz sapabilir.':
    'Some rows this week use a historical date format; the numbers may drift slightly.',
  'Kaynak sekmeler hâlâ düzenlenebilir; hafta kapanana kadar sayılar değişebilir.':
    'The source tabs are still editable; the numbers can change until the week closes.',
  'Haftalık rapor yok': 'No weekly report',
  'Haftalık rapor yalnızca Vegas ve Prime için üretilir; üstteki marka filtresinden birini seçin.':
    'The weekly report is produced for Vegas and Prime only; pick one from the brand filter above.',
  // para şeridi
  'Yatırım (24s)': 'Deposits (24h)',
  'Yatırım (48s)': 'Deposits (48h)',
  'Tekilleştirilmiş:': 'Deduplicated:',
  'Site katkısı (24s)': 'Site contribution (24h)',
  'Site katkısı (48s)': 'Site contribution (48h)',
  'BackOffice verisi yok': 'No BackOffice data',
  'Ekip başarı ort. (24s)': 'Team success avg. (24h)',
  'Ekip başarı ort. (48s)': 'Team success avg. (48h)',
  'WhatsApp - Telegram': 'WhatsApp - Telegram',
  'Cevaplama:': 'Answer rate:',
  // pic 1: Toplam Çağrı = tüm satırlar (Arama Yapılmadı dahil), Yanıtlayan = Yanıtladı+Meşgul+Memnun Değil.
  'Toplam Çağrı / Yanıtlayan Sayısı': 'Total Calls / Answered',
  // liste tablosu
  'Liste Kırılımı': 'List Breakdown',
  'Sekme × sonuç dağılımı · Arama Talepleri kaynağına göre bölünür':
    'Tab × result distribution · split by Call Requests source',
  // PNG butonu title="" (translateDOM [title]); PNG şeridinin "Haftalık Rapor"u yukarıdaki anahtarla çevrilir.
  'Ekran görüntüsü olarak indir': 'Download as a screenshot',
  Toplam: 'Total',
  'YATIRIM PAYI 24S': 'DEPOSIT SHARE 24H',
  'YATIRIM PAYI 48S': 'DEPOSIT SHARE 48H',
  'GÖRÜŞÜLEN ÜYE': 'MEMBERS ENGAGED',
  // Toplam satırı + her liste satırının TOPLAM'ı "Arama Yapılmadı"yı saymaz (gerçek arama
  // değil); sonuç sütunları ham sayıyı gösterir. TOPLAM = Σ sonuç sütunu − Arama Yapılmadı.
  'TOPLAM ve cevaplama oranı "Arama Yapılmadı" satırlarını (gerçek arama değil) saymaz; sonuç sütunları ham sayıyı gösterir. Hiç arama çıkmamış listenin cevaplama oranı BOŞ kalır; Toplam oranı havuzlanmıştır (satır ortalaması değil), o yüzden boş listeler onu aşağı çekmez. BAŞARI = yatırım yapan üye ÷ görüşülen üye (Temsilci Karnesi\'ndeki tanım); Toplam satırında havuzlanmıştır.':
    'TOTAL and answer rate exclude "no call" rows (not real calls); result columns show raw counts. '
    + 'A list with no calls at all shows a BLANK answer rate; the Total rate is pooled (not an average of the rows), so empty lists cannot drag it down. '
    + 'SUCCESS = members who deposited ÷ members engaged (the Agent Scorecard definition); pooled on the Total row.',
  // SMS sayaçları + "SMS başlıkları" halkası (görev #189). "…'den beri" tarih taşır → translatePhrase regex'i.
  'Toplam SMS': 'Total SMS',
  'Temsilci SMS': 'SMS by agents',
  'Otomatik SMS': 'Automatic SMS',
  'Bu markada otomatik SMS yok': 'No automatic SMS on this brand',
  'Henüz veri gelmedi': 'No data received yet',
  'Otomatik SMS okunamadı': 'Automatic SMS could not be read',
  'SMS kayıtları okunamadı': 'SMS records could not be read',
  'Otomatik SMS dahil değil': 'Automatic SMS not included',
  'SMS başlıkları': 'SMS titles',
  'Gönderilen SMS · şablon': 'Sent SMS · template',
  'Otomatik · Kayıt': 'Automatic · Registration',
  'Kayıt SMS:': 'Registration SMS:',
  // Bütün Aramalar (görev #193/#194). Sütun başlıkları (TARİH/SAAT/TEMSİLCİ/LİSTE/SONUÇ/KONUŞMA/YORUM/KAYNAK) ve
  // Araç/Manuel/MARKA/MÜŞTERİ ID zaten sözlükte; sayı/ID taşıyan dizgiler translatePhrase'de regex.
  'Bütün Aramalar': 'All Calls',
  'Müşteri ID ile o müşteriye yapılan bütün çağrılar': 'Every call made to a customer, by customer ID',
  'Müşteri ID': 'Customer ID', // placeholder=""
  Ara: 'Search',
  'Toplam çağrı': 'Total calls',
  'İlk çağrı': 'First call',
  'Çağrı bulunamadı': 'No calls found',
  'Sonuç kaydedilmedi': 'No outcome saved',
  Bağlandı: 'Connected', // title=""
  Bağlanmadı: 'Not connected', // title=""
  'Geçersiz müşteri ID': 'Invalid customer ID',
  'Bu marka için arama yapılamaz': 'This brand cannot be searched',
  'Bu markayı görme yetkiniz yok': 'You do not have access to this brand',
  "Müşteri ID'sini yazıp Ara'ya basın. O müşteriye yapılan her çağrı — hangi temsilci, hangi liste, sonuç, konuşma süresi ve yorum — en yeniden eskiye listelenir.":
    'Type the customer ID and press Search. Every call made to that customer — agent, list, result, talk time and comment — is listed newest first.',
  '"Sonuç kaydedilmedi" = softphone ile arandı ama sonuç girilmedi. Bot = DepositWatchdog satırı, temsilcinin çağrısı değil.':
    '"No outcome saved" = dialled from the softphone but no result was entered. Bot = a DepositWatchdog row, not the agent\'s call.',
  'Otomatik · FTD': 'Automatic · FTD',
  // dağılımlar (halka grafikler)
  Dağılımlar: 'Distributions',
  'Çağrı ve talep sebebi kırılımları': 'Call and request-reason breakdowns',
  'Tüm listeler': 'All lists',
  'Bonus listeleri': 'Bonus lists',
  'Slack sebepleri': 'Slack reasons',
  'Form sebepleri': 'Form reasons',
  'Takip sebepleri': 'Takip reasons',
  // İki dilde de aynı — kalıp eşleşmesi olmasa da düğüm değişmezdi; yine de açıkça
  // yazılıyor ki "bu dizge unutuldu mu?" sorusu bir daha sorulmasın.
  'WhatsApp / Telegram': 'WhatsApp / Telegram',
  'Çağrı payı': 'Share of calls',
  'Talep sebebi': 'Request reason',
  'Mesaj sebebi': 'Message reason',
  // halka efsanesindeki sütun etiketleri (yüzde / adet kolon başlıkları)
  Yüzde: 'Percentage',
  Adet: 'Count',
  // Anahtar EKSİK ile hafta BOŞ farklı şeylerdir: ilki sunucunun henüz göndermediği
  // bir kırılım, ikincisi gerçekten sıfır. İkincisi mevcut 'Veri yok' satırını kullanır.
  'Bu kırılım henüz sunucudan gelmiyor.': 'This breakdown does not come from the server yet.',
  // temsilci karnesi
  'Temsilci Karnesi': 'Agent Scorecard',
  'Görüşme başına yatırım dönüşümü': 'Deposit conversion per conversation',
  '24 saatlik tablo': '24-hour table',
  '48 saatlik tablo': '48-hour table',
  'Yatırıma göre sıralı': 'Sorted by deposits',
  'Skora göre sıralı · QMS %50 · Başarı oranı %40 · Yatırım %10 · Kritik hata −0,1':
    'Sorted by score · QMS 50% · Success rate 40% · Deposits 10% · Critical mistake −0.1',
  'YANITLAYAN ÜYE': 'ANSWERED',
  'YATIRIM YAPAN ÜYE': 'DEPOSITED',
  'BAŞARI ORANI': 'SUCCESS RATE',
  // 'YATIRIM' TEK BAŞINA olamaz: aynı ada sahip bir SEKME var ve liste sütununda
  // verbatim basılıyor — sözlüğe girerse o sheet değeri de çevrilirdi.
  'YATIRIM TUTARI': 'DEPOSIT AMOUNT',
  // QMS sütunları (2026-09-06) — qms-panel'den gelen kalite ortalaması ve kritik hata sayısı.
  'QMS KALİTE': 'QMS SCORE',
  'KRİTİK HATA': 'CRITICAL',
  'Bu hafta değerlendirme yok': 'No evaluation this week',
  'Ekip toplamı': 'Team total',
  // Haftanın Temsilcisi sıralaması (görev #143/#144) — hücre title'ları sayı taşır, sözlüğe girmez.
  'Haftanın Temsilcisi · 24 saat': 'Agent of the Week · 24h',
  'Haftanın Temsilcisi · 48 saat': 'Agent of the Week · 48h',
  'QMS %50 · Başarı oranı %40 · Yatırım %10 · Kritik hata −0,1': 'QMS 50% · Success rate 40% · Deposits 10% · Critical mistake −0.1',
  SIRA: 'RANK',
  'QMS PUANI': 'QMS POINTS',
  'BAŞARI PUANI': 'SUCCESS POINTS',
  'YATIRIM PUANI': 'DEPOSIT POINTS',
  'KRİTİK CEZA': 'CRITICAL PENALTY',
  SKOR: 'SCORE',
  'QMS puanı = QMS ortalaması ÷ 2 (en fazla 5). Başarı puanı haftanın en iyi başarı oranına oranlanır (zirve 4). Yatırım puanı her tam 100.000 TL için 0,20 (en fazla 1,00). Her kritik hata skordan 0,10 düşer. Skor = toplam, 10 üzerinden (en az 0). Değerlendirmesi olmayan temsilci 0 QMS puanı alır. Skor dışı temsilciler listede yer almaz.':
    'QMS points = QMS average ÷ 2 (max 5). Success points are relative to the week’s best success rate (top = 4). Deposit points: 0.20 for every full 100,000 TL (max 1.00). Each critical mistake takes 0.10 off the score. Score = total, out of 10 (never below 0). An agent with no evaluation gets 0 QMS points. Agents excluded from scoring are not listed.',
  // marka yuvası hatası (rapor GENELİ ayakta kalır, yalnız o marka düşer)
  'Rapor hesaplanamadı': 'Report could not be computed',
  'BackOffice erişim anahtarları sunucuda tanımlı değil.': 'BackOffice access keys are not configured on the server.',
  'Bu marka için haftalık rapor üretilemedi.': 'The weekly report could not be produced for this brand.',

  // durum etiketleri (TR markalar)
  // 2026-09-09'da eklenen 9-kanon sistemi: her yeni TR etiketin EN kaplaması ŞART,
  // yoksa Liste Kırılımı başlıkları EN modda Türkçe kalır (api.ts STATUS_TR ile birebir).
  Olumlu: 'Positive',
  'Olumsuz / İlgilenmiyor': 'Not Interested',
  'Markayı Duyunca Kapattı': 'Hung Up on Brand',
  'Meşgul / Tekrar Aranacak': 'Call Back',
  'Ulaşılamadı / Yanıt Yok': 'No Answer',
  'Ulaşılamadı / Hat Meşgul': 'Line Busy',
  'Aranmak İstemiyor': 'Do Not Call',
  // 2026-09-14 eklenen iki sonuç
  'Telefonu Başkası Açtı': 'Answered by Someone Else',
  'Konuşma Esnasında Kapattı': 'Hung Up Mid-Call',
  // legacy kanonlar (tarihsel raporlar)
  Yanıtladı: 'Answered',
  Ulaşılamadı: 'Unreachable',
  'Yanlış Numara': 'Wrong Number',
  Meşgul: 'Busy',
  'Memnun Değil': 'Dissatisfied',
  'Arama Yapılmadı': 'No Call',

  // temsilci detayı
  'Temsilci seç': 'Select agent',
  'Temsilci verileri yükleniyor…': 'Loading agent data…',
  'Temsilci yok': 'No agents',
  'Seçili markalarda temsilci bulunamadı.': 'No agents in the selected brands.',
  'Temsilci verisi alınamadı': 'Could not load agent data',
  'Bu temsilci kapsamınızda olmayabilir ya da kaydı bulunamadı. Listeden başka bir temsilci seçin.':
    'This agent may be outside your scope, or the record was not found. Pick another agent from the list.',
  'TOPLAM ÇAĞRI': 'TOTAL CALLS',
  'İLK / SON ÇAĞRI': 'FIRST / LAST CALL',
  'TERMİNAL AÇILIŞ': 'TERMINAL OPEN',
  'MOLA KULLANIMI': 'BREAK USAGE',
  'DARK KOD': 'DARK CODE',
  'Gün zaman çizelgesi': 'Day timeline',
  '30+ dk boşluk': '30+ min gap',
  'Terminal açılışı': 'Terminal opening',
  // Haftalık istatistik kartı (2026-09-13, gün zaman çizelgesinin yerine)
  'Haftalık istatistik': 'Weekly stats',
  'Arama · Yanıtlanan: panel': 'Calls · Answered: panel',
  'Görüşme · Yatırım · Başarı: haftalık rapor': 'Conversations · Deposits · Success: weekly report',
  GÜN: 'DAY',
  ARAMA: 'CALLS',
  YANITLANAN: 'ANSWERED',
  GÖRÜŞME: 'CONVERSATIONS',
  'YATIRIM 24S': 'DEPOSIT 24H',
  'YATIRIM 48S': 'DEPOSIT 48H',
  'BAŞARI 24S': 'SUCCESS 24H', // görüşme → yatırım oranı (yatırım yapan / görüşme)
  'BAŞARI 48S': 'SUCCESS 48H',
  'Yatırım yapan / görüşme': 'Depositors / conversations', // title=""
  'Yatırım yapan / görüşme (DAVET hariç)': 'Depositors / conversations (DAVET excluded)', // title="" (#297)
  // Liste Detayı (görev #222 + kopyaları, 2026-10-14) — sekme adları/sebepler sheet metni, çevrilmez.
  'Liste Detayı': 'List Detail',
  'Listelerin günlük, haftalık ve aylık istatistikleri': 'Daily, weekly and monthly statistics for every list',
  Listeler: 'Lists',
  'SMS ORANI': 'SMS RATE',
  'Arama sebebine göre': 'By call reason',
  'Sebebe göre sonuçlar': 'Results by reason', // #276
  'Üçü birden': 'All three',
  TALEP: 'REQUESTS',
  'Sekme bulunamadı': 'Tab not found', // title=""
  'Marka seçilmedi': 'No brand selected',
  'Arama Yapılmadı hariç': 'Excluding No Call', // title=""
  'Yanıtlanan / çağrı': 'Answered / calls', // title=""
  'SMS / çağrı': 'SMS / calls', // title=""
  'Çağrı · görüşme · yatırım: haftalık rapor': 'Calls · conversations · deposits: weekly report',
  'SMS: panel (çağrıya bağlanan)': 'SMS: panel (matched to the call)',
  'Bazı haftalar şu an hesaplanıyor — boş günler birazdan kendiliğinden dolar.':
    'Some weeks are being computed right now — the blank days fill in by themselves shortly.',
  'Bu dönemin bir kısmı okunamadı — boş günler bilinmiyor (0 değil).':
    'Part of this period could not be read — blank days are unknown (not 0).',
  'SMS verisi okunamadı — SMS sütunları boş.': 'SMS data could not be read — the SMS columns are blank.',
  'Haftalık rapor şu an başka bir hafta için hesaplanıyor.': 'The weekly report is computing another week right now.',
  'Liste verisi alınamadı.': 'List data could not be loaded.',
  'Amber tutar: bazı yatırım hücreleri henüz boş — rakam artabilir.':
    'Amber amount: some deposit cells are still empty — the figure can still grow.',
  'Listeye bağlanamayan SMS': 'SMS not matched to a list',
  'Bu günün haftası henüz okunamadı': 'This day’s week has not been read yet', // title=""
  'Bu dönemde çağrı yok.': 'No calls in this period.',
  'Bu dönemde sebep girilmiş çağrı yok.': 'No calls with a reason in this period.',
  'TALEP: o sebeple girilmiş her satır (Arama Yapılmadı dahil) · ÇAĞRI: yapılan arama':
    'REQUESTS: every row entered with that reason (incl. No Call) · CALLS: calls actually made',
  // Bonus Kullanımı (sahip 2026-10-27) — sekme adları (BONUS / RETENTION / CHURN) sheet metni, çevrilmez.
  // Sayı taşıyan parçalar ayrı düğümde basılır ("{n}" + "görüşme okunamadı"), sözlüğe sayı girmez.
  'Bonus Kullanımı': 'Bonus Usage',
  'Bonus listesinde aramadan sonraki 48 saatte bonusu kullananlar': 'Bonus-list customers who used the bonus within 48 hours of the call',
  'Kaynak: BackOffice bonus listesi, her görüşmenin 48. saatinde okunur': 'Source: BackOffice bonus list, read at each conversation’s 48th hour',
  'Bonus kullanım verisi alınamadı.': 'Bonus usage data could not be loaded.',
  'Kullandı = aramadan sonraki 48 saatte listenin yüklü bonusunda çevrim başladı ya da yeni bir bonus alındı. Oran yalnız 48 saati dolmuş görüşmeler üzerinden.':
    'Used = within 48 hours of the call, wagering started on the list’s loaded bonus or a new bonus was claimed. The rate counts only conversations whose 48 hours have passed.',
  'Henüz veri yok — ilk kontroller birkaç dakika içinde başlar.': 'No data yet — the first checks start within a few minutes.',
  'Veri başlangıcı': 'Data starts',
  'görüşme okunamadı': 'conversations could not be read',
  'BackOffice hesabının bonus listesi yetkisi yok': 'the BackOffice account has no permission for the bonus list',
  'Bu markada BackOffice hesabı tanımlı değil': 'no BackOffice account is configured for this brand',
  'BackOffice şu an yanıt vermiyor (sınır / Cloudflare)': 'the BackOffice is not answering right now (rate limit / Cloudflare)',
  'BackOffice okuması başarısız': 'the BackOffice read failed',
  'BackOffice çevrim bilgisini göndermedi': 'the BackOffice did not send the wagering figure',
  'görüşme 48 saatten sonra okundu (ilk kurulum) — sonradan kullanım da sayılmış olabilir.':
    'conversations were read after their 48 hours (first setup) — later usage may be included.',
  'KONTROL EDİLEN': 'CHECKED',
  KULLANAN: 'USED',
  'KULLANIM 48S': 'USAGE 48H',
  BEKLEYEN: 'WAITING',
  OKUNAMADI: 'UNREAD',
  'Olumlu · Olumsuz · Aranmak İstemiyor · Konuşma Esnasında Kapattı': 'Positive · Not interested · Do not call · Hung up mid-call', // title=""
  "48 saati dolmuş ve BackOffice'ten okunmuş": 'Past its 48 hours and read from the BackOffice', // title=""
  'Kullanan / kontrol edilen': 'Used / checked', // title=""
  '48 saat henüz dolmadı': '48 hours not over yet',
  '48 saat doldu ama BackOffice okunamadı': '48 hours over but the BackOffice could not be read', // title=""
  'Temsilcilere göre': 'By agent',
  'Bu dönemde bonus listesinde görüşme yok.': 'No bonus-list conversations in this period.',
  'Bu gün özellik başlamadan önce': 'This day is before the feature started', // title=""
  Pzt: 'Mon',
  Sal: 'Tue',
  Çar: 'Wed',
  Per: 'Thu',
  Cum: 'Fri',
  Cmt: 'Sat',
  Paz: 'Sun',
  'Haftalık rapor şu an başka bir hafta için hesaplanıyor — görüşme ve yatırım sütunları birazdan gelir.':
    'The weekly report is busy computing another week — the conversation and deposit columns will follow shortly.',
  'Haftalık veri alınamadı.': 'Weekly data could not be loaded.',
  'Yatırım tutarları henüz kesinleşmedi (48 saatlik pencereler açık) — rakamlar değişebilir.':
    'Deposit amounts are not final yet (48-hour windows still open) — figures may change.',
  "Bu temsilcinin adı haftalık raporda bulunamadı — Ayarlar'dan takma adını ekleyin.":
    "This agent's name was not found in the weekly report — add their alias in Settings.",
  'Bu temsilci haftalık rapordan hariç tutuluyor (çağrı ekibinde değil).': 'This agent is excluded from the weekly report (not on the call team).',
  'Görüşme ve yatırım verisi yalnız Vegas ve Prime için gelir.': 'Conversation and deposit data is available for Vegas and Prime only.',
  'Haftalık rapor okunamadı — görüşme ve yatırım sütunları boş.': 'The weekly report could not be read — conversation and deposit columns are empty.',
  'Çağrı günlüğü': 'Call log',
  kayıt: 'records', // "{n} kayıt" sayacında " kayıt" AYRI bir düğüm — sayı kendi span'ında
  SAAT: 'TIME',
  LİSTE: 'LIST',
  'MÜŞTERİ ID': 'CUSTOMER ID',
  SONUÇ: 'RESULT',
  YORUM: 'COMMENT',
  KAYNAK: 'SOURCE',
  Araç: 'Tool',
  Manuel: 'Manual',
  'Bekleme süreleri': 'Wait times',
  'Sonuç tipine göre ort. / medyan': 'By result type avg / median',
  'ORT.': 'AVG',
  'MED.': 'MEDIAN',
  ADET: 'COUNT',
  'DARK karşılaştırma': 'DARK comparison',
  TERMİNAL: 'TERMINAL',
  Fark: 'Difference',
  'DARK konuşma süresi': 'DARK talk time',
  // 2026-10: getircall "Toplam Arama Süresi" çalma DAHİL — etiket düzeltildi, eski satır kalır
  // (cache'lenmiş eski SPA paketi için zararsız).
  'DARK toplam arama süresi': 'DARK total call time',
  'Panel çalma + konuşma': 'Panel ring + talk',
  // Softphone ölçümü (call_sessions, migration 029): çağrı günlüğü, haftalık kart, Döngü,
  // mutabakat. Sayı/saat taşıyan title'lar translatePhrase'de regex.
  'Softphone: bağlanan çağrıların konuşma süresi': 'Softphone: talk time of connected calls',
  Döngü: 'Cycle',
  'Eklenti ölçümü · p50 / p95': 'Extension-measured · p50 / p95',
  METRİK: 'METRIC',
  'Müşteri başına (gösterim → kayıt)': 'Per customer (shown → saved)',
  'Müşteri bilgisi getirme': 'Customer lookup',
  'Sayfa yenileme': 'Page reload',
  'Arama ↔ sonuç mutabakatı': 'Call ↔ outcome reconciliation',
  'Softphone araması': 'Softphone calls',
  'Araçla kaydedilen sonuç': 'Outcomes saved in the tool',
  'Sonucu girilmemiş arama': 'Calls without an outcome',
  'Araması görünmeyen sonuç': 'Outcomes without a call',
  Sürüyor: 'Ongoing',
  Cevapsız: 'Unanswered',
  // Raporlar — softphone sütunları + ekip kartı
  BAĞLANMA: 'CONNECT RATE',
  'ORT. KONUŞMA': 'AVG TALK',
  'Softphone: bağlanan / arama': 'Softphone: connected / dials',
  'Softphone: bağlanan çağrı başına konuşma': 'Softphone: talk per connected call',
  'Softphone ölçümü': 'Softphone measurement',
  'Seçili dönem · ekip toplamı': 'Selected period · team total',
  BAĞLANAN: 'CONNECTED',
  'TOPLAM KONUŞMA': 'TOTAL TALK',
  'DÖNGÜ P50': 'CYCLE P50',
  'DÖNGÜ P95': 'CYCLE P95',
  // Haftalık Rapor — Temsilci Karnesi konuşma sütunu
  'KONUŞMA SÜRESİ': 'TALK TIME',
  'Softphone: bağlanan çağrıların toplam konuşma süresi': 'Softphone: total talk time of connected calls',
  // Canlı Durum — ölçülen çağrı alt satırı (sayaç ayrı düğüm)
  'Aranıyor…': 'Dialling…',
  Aramada: 'On call',
  'TERMİNAL İLK/SON': 'TERMINAL FIRST/LAST',
  'DARK İLK/SON': 'DARK FIRST/LAST',
  'Mola öncesi': 'Before break',
  'Geri arama istedi': 'Requested call back',
  'Yatırım sözü verdi': 'Promised deposit',
  İlgilenmedi: 'Not interested',
  'Numara hatalı': 'Wrong number',
  'Bonus talep etti': 'Asked for bonus',
  'Daha sonra aranacak': 'Call later',
  'Memnuniyetsizlik iletildi': 'Complaint noted',
  'Akşam tekrar denenecek': 'Retry in the evening',
  'Kayıt tamamlandı': 'Registration done',
  'Promosyon bilgilendirildi': 'Promo informed',

  // molalar
  'Şu An Molada': 'Currently On Break',
  canlı: 'live',
  'Şu anda molada olan temsilci yok.': 'No agents are currently on break.',
  'GEÇEN SÜRE': 'ELAPSED',
  'Başl.': 'Start', // "Başl. {saat}" — saat AYRI düğüm, kısaltma tek başına eşleşir
  'Mola verileri yükleniyor…': 'Loading break data…',
  'Kayıt yok': 'No records',
  'Günlük mola kullanımı': 'Daily break usage',
  '· hak 60 dk + ek': '· allowance 60 min + extra',
  KULLANILAN: 'USED',
  HAK: 'ALLOWANCE',
  KALAN: 'REMAINING',
  AŞIM: 'OVERAGE',
  OTURUM: 'SESSIONS',
  'Bitiş:': 'End:',
  Temsilci: 'Agent',
  'Zaman aşımı': 'Timeout',
  Admin: 'Admin',
  'Gece yarısı': 'Midnight',
  'Bu gün mola kaydı yok.': 'No break records for this day.',
  'Mola Süresi Ekle': 'Add Break Time',
  'Temsilciye ek mola hakkı tanımla': 'Grant extra break allowance to an agent',
  'EK SÜRE (DAKİKA)': 'EXTRA TIME (MINUTES)',
  İptal: 'Cancel',
  'Onayla ve ekle': 'Confirm and add',
  'Ekleniyor…': 'Adding…',
  Yoğunluk: 'Workload',
  'Sağlık molası': 'Health break',
  'Yönetici onayı': 'Manager approval',
  'Teknik sorun': 'Technical issue',
  Diğer: 'Other',

  // uyarılar — backend'in ÜRETTİĞİ statik dizgiler (dinamik olanlar translatePhrase'de regex)
  'Terminalden bilinmeyen temsilci adı geldi': 'Unknown agent name received from terminal',
  'Bilinmeyen durum değeri geldi': 'Unknown status value received',
  Ad: 'Name',
  Sekme: 'Tab',
  'Uyuşmayan satır': 'Mismatched rows',
  Değer: 'Value',
  // vardiya planı uyarıları (kural başlıkları + payload etiketleri)
  'Vardiyaya gelmedi': 'No-show',
  'Vardiya planında eşleşmeyen isim': 'Unmatched name in shift roster',
  'Vardiya planı okunamadı': 'Shift roster could not be read',
  'Vardiya planındaki isim hiçbir temsilciyle eşleşmedi': 'A name in the shift roster matched no agent',
  'Vardiya planı bu haftaya güncellenmemiş': 'Shift roster has not been updated for this week',
  'Vardiya planında tarih başlığı okunamadı': 'Date headers in the shift roster could not be read',
  // NOT: Başlangıç / Bugün / Uyarılar sözlükte ZATEN var (sırasıyla 'Start', 'Today',
  // 'Alerts'). Tekrar tanımlanamaz — payload etiketleri de o karşılıklara düşer, bu
  // yüzden plan sorunları için 'Uyarılar' değil 'Sorunlar' etiketi kullanılır.
  Vardiya: 'Shift',
  Görev: 'Task',
  Tolerans: 'Grace',
  // Yalnızca gelen-çağrı penceresi vardiya başlangıcını ötelediğinde basılır
  // (BetBooker pazar 14:00 Accra — src/engine/inbound-hours.js).
  'Arama beklenen saat': 'Dialling expected from',
  Hafta: 'Week',
  Plan: 'Roster',
  Çağrı: 'Calls',
  'Plandaki günler': 'Days in roster',
  Aralık: 'Range',
  Sorunlar: 'Issues',

  // uyarılar
  'Tüm kural tipleri': 'All rule types',
  'Tüm temsilciler': 'All agents',
  'Tüm markalar': 'All brands',
  'Tüm durumlar': 'All statuses',
  Yeni: 'New',
  // "{n} seçili · {m} yeni" toplu seçim şeridi: kalın sayı span'ı dizgiyi böler —
  // "seçili ·" kendi düğümü, "{m} yeni" tek düğüm olarak birleştirildi (regex çevirir).
  'seçili ·': 'selected ·',
  Onaylandı: 'Approved',
  'Onaylandı:': 'Approved:',
  // Ayrıntı panelinde damganın etiketi: "Oluşturuldu: 29.07 10:25" — etiket kendi metin
  // düğümünde kalır (damga ayrı düğüm), bu yüzden anahtar iki nokta ile biter.
  'Oluşturuldu:': 'Created:',
  Temizle: 'Clear',
  'Uyarılar yükleniyor…': 'Loading alerts…',
  'Uyarı yok': 'No alerts',
  'Seçili filtrelerle eşleşen uyarı yok.': 'No alerts match the selected filters.',
  'Tümünü seç': 'Select all',
  'Seçilenleri Onayla': 'Approve selected',
  'Onaylanıyor…': 'Approving…',
  Onayla: 'Approve',
  // Onay açıklaması modalı + ayrıntı panelindeki açıklama kutusu (migration 026).
  'Onay açıklaması': 'Approval note',
  'Bu uyarıyı neden onayladığınızı yazın. Açıklamasız onay kaydedilmez.':
    'Explain why you are approving this alert. An approval without a note is not saved.',
  'Açıklama (zorunlu)': 'Note (required)', // placeholder — translateDOM textarea'ları da çevirir
  Vazgeç: 'Cancel',
  'Onay kaydedilemedi. Tekrar deneyin.': 'The approval could not be saved. Try again.',
  'Mola öncesi boşta kalma': 'Idle before break',
  'Mola hakkı aşıldı': 'Break allowance exceeded',
  'DARK çağrı sayısı uyuşmazlığı (>20)': 'DARK call count mismatch (>20)',
  'DARK raporu yüklenmedi': 'DARK report not uploaded',
  // 'Uzun boşluk (>N dk)' sözlükte DEĞİL: N canlı thresholds.longGapMin'dir
  // (src/engine/status-map.js alertRuleTitle) ve sabit bir satır yalnızca N=30'u
  // yakalardı — 15'e çekildiği gün başlık EN modunda sessizce Türkçe kalırdı.
  // translatePhrase'in regex'i çevirir.
  'Eşleşmeyen temsilci adı': 'Unmatched agent name',
  'Sheet senkronu donduruldu': 'Sheet sync frozen',
  'Mola otomatik kapatıldı': 'Break auto-closed',
  'Bilinmeyen durum değeri': 'Unknown status value',
  // saat_sapmasi (migration 012) — kural başlığı, açıklaması ve payload etiketi. Payload
  // DEĞERİ sayı taşır ("60 dk geri"), o yüzden sözlükte değil, translatePhrase'de regex.
  'Bilgisayar saati sapması': 'Workstation clock skew',
  'Temsilcinin bilgisayar saati sunucu saatinden sapıyor':
    "The agent's workstation clock is drifting from the server clock",
  Sapma: 'Skew',
  // sonuc_kaybi (migration 019/020) — kural başlığı + payload etiketi. AÇIKLAMASI sayı taşır
  // ("3 çağrı sonucu panele hiç ulaşmadı"), o yüzden sözlükte değil, translatePhrase'de regex.
  'Çağrı sonucu kaybı': 'Call result lost',
  'Kayıp sonuç': 'Lost results',
  // dahili_uyusmazligi (migration 029) — kural başlığı + payload etiketleri. AÇIKLAMA KOD/ad
  // taşır ("Kullanılan KOD 692 · Panel KOD 662"), o yüzden translatePhrase'de parça parça regex.
  'Dahili (KOD) uyuşmazlığı': 'Extension (code) mismatch',
  'Kullanılan KOD': 'Code used',
  'Panel KOD': 'Panel code',
  'Kayıt KOD': 'Registry code',
  'Aynı KOD kullanan': 'Same code used by',
  // cagri_hedefi_alti / dusuk_yanit_orani (migration 031) — kural başlıkları + payload etiketleri.
  // AÇIKLAMALAR sayı taşır ("245 çağrı — günlük hedef 300"), o yüzden translatePhrase'de regex.
  'Günlük çağrı hedefinin altında': 'Below the daily call target',
  'Düşük yanıtlanma oranı': 'Low answer rate',
  'Günlük hedef': 'Daily target',
  'Hedefe kalan': 'Short of target',
  Yanıtlanan: 'Answered',
  'Yanıtlanma oranı': 'Answer rate',
  'Alt sınır': 'Floor',
  // dark_uyusmazligi'nin arama ayağı (thresholds.darkCompareDials) payload etiketleri.
  Arama: 'Dials',
  'DARK giden': 'DARK outbound',
  'Arama farkı': 'Dial difference',
  // mola_otomatik_kapandi açıklamaları (jobs/break-sweeper.js REASON_DESC — backend üretir)
  'Gece yarısı sınırında otomatik kapatıldı': 'Auto-closed at the midnight boundary',
  '3 saati aştığı için otomatik kapatıldı': 'Auto-closed after exceeding 3 hours',
  Bilgi: 'Info',
  Uyarı: 'Warning',
  Kritik: 'Critical',
  'Son çağrı': 'Last call',
  'Sonraki çağrı': 'Next call',
  Neden: 'Reason',
  'Mola başlangıcı': 'Break start',
  'Boşta süre': 'Idle time',
  Departman: 'Department',
  Kullanılan: 'Used',
  Hak: 'Allowance',
  Aşım: 'Overage',
  'Terminal toplam': 'Terminal total',
  'DARK toplam': 'DARK total',
  Eşik: 'Threshold',
  Beklenen: 'Expected',
  Kaynak: 'Source',
  Marka: 'Brand',
  Durum: 'Status',
  Başlangıç: 'Start',
  Bitiş: 'End',
  Süre: 'Duration',
  'Ham ad': 'Raw name',
  Satır: 'Row',
  Sheet: 'Sheet',
  Donduran: 'Frozen by',
  Saat: 'Time',
  Eksik: 'Missing',
  Donduruldu: 'Frozen',

  // dark
  'DARK karşılaştırması yükleniyor…': 'Loading DARK comparison…',
  'getircall PBX rapor yükleme': 'getircall PBX report upload',
  'RAPOR GÜNÜ': 'REPORT DAY',
  'Raporu sürükleyip bırakın': 'Drag and drop the report',
  'veya seçmek için tıklayın · .xlsx / .csv': 'or click to select · .xlsx / .csv',
  'Yükleniyor…': 'Uploading…',
  'YÜKLEME GEÇMİŞİ': 'UPLOAD HISTORY',
  Gün: 'Day', // "Gün {tarih} · {n} satır" yükleme geçmişi — interpolasyon düğümleri böler
  satır: 'rows',
  'Terminal ↔ DARK karşılaştırma': 'Terminal ↔ DARK comparison',
  '· mutlak fark >20 kırmızı': '· absolute diff >20 red',
  FARK: 'DIFF',
  KONUŞMA: 'TALK',
  'İLK ÇAĞRI': 'FIRST CALL',
  'SON ÇAĞRI': 'LAST CALL',
  "Eşleşmeyen DARK KOD'ları": 'Unmatched DARK Codes',
  'Sistemde temsilci kaydı bulunmayan kodlar': 'Codes with no agent record in the system',
  'çağrı ·': 'calls ·', // "{n} çağrı · {süre}" — interpolasyon düğümleri böler (satır/temsilci gibi)
  "Ayarlar'da eşleştir": 'Match in Settings',

  // ayarlar
  Kullanıcılar: 'Users',
  Temsilciler: 'Agents',
  Eşikler: 'Thresholds',
  'Durum Kategorileri': 'Status Categories',
  'Ayarlar yükleniyor…': 'Loading settings…',
  'Panel Kullanıcıları': 'Panel Users',
  'Erişim, rol ve marka atamaları': 'Access, role and brand assignments',
  'Yeni Kullanıcı': 'New User',
  KULLANICI: 'USER',
  ROL: 'ROLE',
  MARKALAR: 'BRANDS',
  AKTİF: 'ACTIVE',
  İŞLEM: 'ACTIONS',
  Düzenle: 'Edit', // title=""
  'Şifre sıfırla': 'Reset password', // title=""
  'Temsilci Kayıtları': 'Agent Records',
  'Sarı satırlar otomatik oluşan geçici kayıtlardır': 'Yellow rows are auto-created temporary records',
  AD: 'NAME',
  geçici: 'temp',
  'takım lideri': 'team lead',
  'Takım lideri: bu temsilciden hiçbir uyarı üretilmez':
    'Team lead: no alerts are raised for this agent',
  "Bu geçici kayıt, sheet'te eşleşmeyen bir addan otomatik oluştu.": 'This temporary record was auto-created from an unmatched sheet name.',
  'Gerçek temsilci, onayla': 'Real agent, confirm',
  'Mevcut temsilciyle birleştir': 'Merge with existing agent',
  'AD (İSİM)': 'NAME',
  'MARKA ATAMASI': 'BRAND ASSIGNMENT',
  'TERMİNAL AD EŞLEŞMELERİ (ALIAS)': 'TERMINAL NAME MATCHES (ALIAS)',
  '+ alias ekle': '+ add alias',
  'Eşik Değerleri': 'Threshold Values',
  'Uyarı ve metrik hesaplamalarında kullanılır': 'Used in alert and metric calculations',
  'Mola öncesi boşta eşiği': 'Idle-before-break threshold',
  'DARK fark eşiği': 'DARK diff threshold',
  'Uzun boşluk eşiği': 'Long gap threshold',
  'Günlük mola hakkı': 'Daily break allowance',
  'Mola aşımı toleransı': 'Break overrun grace',
  'Vardiyaya gelmedi toleransı': 'No-show grace period',
  'Günlük çağrı hedefi': 'Daily call target',
  'Yanıtlanma oranı alt sınırı': 'Minimum answer rate',
  'Günlük hedef değerlendirme payı': 'Daily target judging delay',
  dk: 'min',
  çağrı: 'calls',
  // ayarlar → vardiya planı kartı
  'Vardiya Planı': 'Shift Roster',
  'Şimdi yenile': 'Refresh now',
  'Bu haftaya güncellenmemiş': 'Not updated for this week',
  'Son okuma:': 'Last read:',
  'Hafta:': 'Week:',
  'Kayıt:': 'Records:',
  'Kişi:': 'People:',
  'Plan yenilendi': 'Roster refreshed',
  'Yenileme başarısız': 'Refresh failed',
  'Tembellik metriği — Türkçe': 'Laziness metric — Turkish',
  'Tembellik metriği — BetBooker': 'Laziness metric — BetBooker',
  'İşaretli sonuçlar metriğe dahildir': 'Checked results are included in the metric',
  'İngilizce sonuç eşdeğerleri': 'English result equivalents',
  'Kullanıcı Düzenle': 'Edit User',
  'Şifre Sıfırla': 'Reset Password',
  'ATANMIŞ MARKALAR': 'ASSIGNED BRANDS',
  Aktif: 'Active',
  'en az 6 karakter': 'min 6 characters',
  'yeni şifre': 'new password',
  Kaydet: 'Save',
  'Kaydediliyor…': 'Saving…',
  'için yeni şifre belirleyin.': '— set a new password.',
  'YENİ ŞİFRE': 'NEW PASSWORD',
  'Geçici temsilciyi birleştir': 'Merge temporary agent',
  "Bu geçici kaydın alias'ları ve markaları seçilen mevcut temsilciye taşınır, geçici kayıt silinir.":
    "This temporary record's aliases and brands move to the selected agent; the temp record is deleted.",
  'HEDEF TEMSİLCİ': 'TARGET AGENT',
  'Temsilci seçin…': 'Select agent…',
  Birleştir: 'Merge',
  'Birleştiriliyor…': 'Merging…',
  // ayarlar → temsilci arama / kaldırma (migration 035)
  Kaldırılanlar: 'Removed',
  'Kaldırılan temsilciler — geçmişleri duruyor, geri alınabilir': 'Removed agents — history is kept, they can be restored',
  'Eşleşen temsilci yok': 'No matching agents',
  'Kaldırılmış temsilci yok': 'No removed agents',
  'Geri al': 'Restore',
  'Temsilciyi kaldır': 'Remove agent', // title="" + modal başlığı
  "Temsilci bu listeden, Canlı Durum'dan ve raporlardan kalkar; açık uyarıları kapanır, yeni uyarı üretilmez. Çağrı, mola ve DARK geçmişi silinmez — Kaldırılanlar listesinden geri alınabilir.":
    'The agent leaves this list, Live Status and the reports; open alerts are closed and no new alerts are raised. Call, break and DARK history is not deleted — restore from the Removed list.',
  Kaldır: 'Remove',
  'Kaldırılıyor…': 'Removing…',
  // ayarlar → İşten ayrıldı (src/api/offboard.js, 2026-11-02). Adım metinleri sunucudan TR gelir.
  'İşten ayrıldı': 'Left the company',
  'Tek seferde: ayrılış gününden itibaren Canlı Durum ve raporlardan çıkar (öncesi raporlarda kalır), açık uyarıları kapanır, eklenti girişi kapanır ve QMS hesabı kapanır. Çağrı, mola ve DARK geçmişi silinmez — Kaldırılanlar listesinden geri alınabilir.':
    'In one step: from the leave date the agent is out of Live Status and the reports (earlier reports keep them), open alerts are closed, the extension login and the QMS account are closed. Call, break and DARK history is not deleted — restore from the Removed list.',
  'AYRILIŞ TARİHİ': 'LEAVE DATE',
  SEBEP: 'REASON',
  Ayrıldı: 'Resigned',
  Çıkarıldı: 'Dismissed',
  'ONAY İÇİN ADINI YAZIN': 'TYPE THEIR NAME TO CONFIRM',
  "Elle kalanlar: getircall'da dahiliyi boşaltın · BackOffice kullanıcısını kapatın.":
    'Still manual: free the extension in getircall · close the BackOffice user.',
  'İşleniyor…': 'Working…',
  'İşten mi ayrıldı?': 'Did they leave the company?',
  'Ayrıldıysa "İşten ayrıldı" kullanın: geçmiş raporlarda kalır, eklenti ve QMS girişi de kapanır. Pasif yapmak temsilciyi geçmiş raporlardan da çıkarır ve girişlerine dokunmaz.':
    'If they left, use "Left the company": they stay in past reports and their extension and QMS logins are closed too. Making them inactive also removes them from past reports and leaves their logins open.',
  'Sadece pasif yap': 'Only make inactive',
  // ayarlar → mutasyon hataları. Sunucu bunları HTTP 200 + {ok:false,error} ile döner
  // (src/api/settings.js); satır/modal altında ErrorLine ile basılır.
  'İşlem başarısız': 'Operation failed',
  'Kullanıcı adı gerekli': 'Username is required',
  'Bu kullanıcı adı zaten var': 'That username already exists',
  'Bu DARK KOD başka bir temsilcide kayıtlı': 'That DARK code belongs to another agent',
  'Bu alias zaten başka bir kayıtta': 'That alias belongs to another record',
  'Kaldırılmış temsilci aktif edilemez — önce geri alın': 'A removed agent cannot be activated — restore it first',
  // ayarlar → panel şifreleri (pages/PanelPasswords.tsx + src/api/panel-passwords.js mesajları)
  'Panel Şifreleri': 'Panel Passwords',
  'Temsilcilerin Call Agent paneline giriş şifreleri': "Agents' Call Agent panel sign-in passwords",
  'Temsilci ara': 'Search agent',
  'Sıfırla: şifre varsayılana döner (AD.123), temsilci ilk girişte yeni şifre belirler. Belirle: girdiğiniz şifre hemen geçerli olur.':
    'Reset: the password returns to the default (NAME.123) and the agent sets a new one at first sign-in. Set: the password you enter works immediately.',
  'SON DEĞİŞİKLİK': 'LAST CHANGE',
  DURUM: 'STATUS',
  Kişisel: 'Personal',
  Varsayılan: 'Default',
  Kilitli: 'Locked',
  'Varsayılana sıfırla': 'Reset to default', // title=""
  'Şifre belirle': 'Set password',
  'Şifreyi sıfırla': 'Reset password',
  'için panel şifresi varsayılana dönecek. Temsilci ilk girişte kendi şifresini belirleyecek.':
    '— the panel password returns to the default. The agent sets their own at first sign-in.',
  'için yeni panel şifresi.': '— new panel password.',
  Gizle: 'Hide', // title=""
  Göster: 'Show', // title=""
  Sıfırla: 'Reset',
  'Şifre sıfırlandı': 'Password reset',
  'GİRİŞ ŞİFRESİ': 'SIGN-IN PASSWORD',
  Kopyala: 'Copy', // title=""
  'Temsilci bu şifreyle girince kendi şifresini belirlemesi istenir.':
    'After signing in with this password the agent is asked to set their own.',
  'Proxy dosyaya yazamadı — değişiklik proxy yeniden başlayana kadar geçerli':
    'The proxy could not write its file — the change lasts until the proxy restarts',
  Tamam: 'OK',
  'Şifre güncellendi': 'Password updated',
  'artık yeni şifreyle giriş yapar.': '— now signs in with the new password.',
  'Atanmış markalarınızda panel girişi yok.': 'None of your brands has panel sign-in.',
  "Proxy'ye ulaşılamadı — tekrar deneyin": 'Could not reach the proxy — try again',
  "Bu markanın proxy'si bu özelliği henüz desteklemiyor (güncelleme gerekli)":
    "This brand's proxy does not support this yet (update needed)",
  'Bu markada telefon listesi tanımlı değil': 'This brand has no phone list',
  'Bu markada panel girişi kapalı': 'Panel sign-in is off for this brand',
  'Proxy anahtarı reddetti (INGEST anahtarı eşleşmiyor)': 'The proxy rejected the key (INGEST key mismatch)',
  'Bu temsilci markanın telefon listesinde yok': "This agent is not in the brand's phone list",
  'Proxy adresi başka bir markaya gidiyor — yapılandırmayı kontrol edin':
    'The proxy URL points at another brand — check the configuration',
  'Çok fazla istek — biraz sonra tekrar deneyin': 'Too many requests — try again shortly',
  'Telefon yapılandırması okunamadı — tekrar deneyin': 'Phone configuration could not be read — try again',
  'Bu marka için proxy adresi/anahtarı tanımlı değil': 'No proxy URL/key configured for this brand',
  'Bu markada panel şifresi yönetimi yok': 'No panel password management for this brand',
  'Geçersiz temsilci adı': 'Invalid agent name',
  'Proxy işlemi onaylamadı': 'The proxy did not confirm the change',
  // panel şifreleri → Yeni temsilci / DARK şifresi (pages/NewHire.tsx + src/api/new-hire.js, 2026-10-14).
  // Ad / KOD taşıyan sunucu mesajları sözlüğe girmez (dinamik) — TR kalır.
  'Yeni temsilci': 'New agent',
  'Panel girişi, telefon (Ara) ve DARK eşleşmesi tek adımda oluşturulur.':
    'Creates the panel sign-in, the phone (Call button) and the DARK mapping in one step.',
  'DAHİLİ (KOD)': 'EXTENSION (CODE)',
  BİRİM: 'UNIT',
  'DARK ŞİFRESİ': 'DARK PASSWORD',
  'DARK ŞİFRESİ (İSTEĞE BAĞLI)': 'DARK PASSWORD (OPTIONAL)',
  'PANEL ŞİFRESİ (İSTEĞE BAĞLI)': 'PANEL PASSWORD (OPTIONAL)',
  'PANEL GİRİŞİ': 'PANEL SIGN-IN',
  'Kaydetmeden önce santralde doğrula': 'Verify with the PBX before saving',
  'VIP / PREVIP paneli kullanmaz — yalnız DARK eşleşmesi; DARK şifresi isteğe bağlı.':
    'VIP / PREVIP do not use the panel — DARK mapping only; the DARK password is optional.',
  "Prime VIP hattı: kayıt sheet'inde PRIME | VIP satırı; temsilci VIP panelinde giriş yapar — DARK şifresi gerekli.":
    'Prime VIP line: a PRIME | VIP row in the registry sheet; the agent signs in to the VIP panel — DARK password required.',
  'ör. HAZAL': 'e.g. HAZAL', // placeholder=""
  'ör. 695': 'e.g. 695', // placeholder=""
  'getircall → Santral → Dahili → Şifre': 'getircall → PBX → Extension → Password', // placeholder=""
  'boş bırakın: AD.123 — ilk girişte değiştirir': 'leave empty: NAME.123 — changed at first sign-in', // placeholder=""
  'Doğrulamadan kaydet': 'Save without verifying',
  'Santral doğrulanıyor…': 'Verifying with the PBX…',
  'Telefon / panel girişi': 'Phone / panel sign-in',
  'Temsilci kaydı': 'Agent record',
  'Panel şifresi': 'Panel password',
  "Kayıt sheet'i": 'Registry sheet',
  'Kimlik bağlama': 'Identity binding',
  'Temsilci belirlediğiniz şifreyle girer.': 'The agent signs in with the password you set.',
  'Temsilci Call Agent panelinde bu ad ve şifreyle girer; ilk girişte kendi şifresini belirler.':
    'The agent signs in to the Call Agent panel with this name and password and sets their own at first sign-in.',
  'Temsilci telefon listesinde zaten vardı — mevcut şifresiyle girer.':
    'The agent was already in the phone list — they sign in with their current password.',
  'DARK şifresi': 'DARK password', // title="" + dialog title
  'için dahilinin yeni SIP şifresi (getircall → Santral → Dahili → Şifre). Dahili numarası değişmez.':
    "— the extension's new SIP password (getircall → PBX → Extension → Password). The extension number stays.",
  'Açık paneller yeni şifreyi en geç bir sonraki girişte alır.': 'Open panels pick up the new password at their next sign-in at the latest.',
  'Ad gerekli': 'Name is required',
  'Dahili numarası 2–6 rakam olmalı (ör. 695)': 'The extension must be 2–6 digits (e.g. 695)',
  'DARK şifresi gerekli — getircall → Santral → Dahili → Şifre': 'DARK password required — getircall → PBX → Extension → Password',
  'DARK şifresi gerekli': 'DARK password required',
  'Panel şifresi en az 6 karakter olmalı': 'The panel password must be at least 6 characters',
  'DARK şifresi güncellendi — santral doğruladı': 'DARK password updated — verified with the PBX',
  'DARK şifresi güncellendi — santral doğrulaması atlandı': 'DARK password updated — PBX check skipped',
  'DARK şifresi zaten bu': 'That is already the DARK password',

  // panel hesabı temsilciyle taşınır (src/api/panel-accounts.js, 2026-10-16) — Ayarlar → Temsilciler +
  // Panel Şifreleri "DİĞER MARKALARDA". "<Marka>: …" adımları translatePhrase'de panelStepEN çevirir.
  'PANEL HESABI': 'PANEL ACCOUNT',
  'Panel hesabını eşitle': 'Sync panel account',
  'Eşitleniyor…': 'Syncing…',
  'Giriş adı, şifre ve dahili seçili markaların paneline taşınır; ayrıldığı markadan kaldırılır':
    "Sign-in name, password and extension move to the selected brands' panels and leave the brand she left",
  'Marka değişince temsilcinin panel girişi (ad, şifre, dahili) yeni markaya taşınır ve ayrıldığı markadan kaldırılır.':
    "When the brand changes, the agent's panel sign-in (name, password, extension) moves to the new brand and is removed from the old one.",
  'DİĞER MARKALARDA': 'ON OTHER BRANDS',
  'Kaldırılmış temsilci — panel hesaplarına dokunulmadı': 'Removed agent — panel accounts left as they are',
  'Test hesabı — panel hesaplarına dokunulmadı': 'Test login — panel accounts left as they are',
  'DARK KOD yok — panel hesabı otomatik taşınamaz (önce KOD girin)': 'No DARK KOD — the panel account cannot move automatically (enter the KOD first)',
  'Panel hesabı eşitlenemedi — tekrar deneyin': 'Panel account could not be synced — try again',
  'bu panelde bağlı değil': 'not connected on this panel',
  'şifre diğer markadakiyle eşitlendi': 'password matched to the other brand',
  'hiçbir panelde hesabı yok — Panel Şifreleri → Yeni temsilci ile ekleyin': 'no account on any panel — add her via Panel Passwords → New agent',
  'hesabı kaldırılmadı — önce yeni markada hesabı açılmalı': 'account not removed — it must exist on the new brand first',
  'hesabı korunuyor — başka panel markası seçili değil': 'account kept — no other panel brand is selected',
  'hesabı kaldırıldı (giriş listesinden çıktı)': 'account removed (off the sign-in list)',
  'zaten aynı': 'already the same',
  'aynısı uygulandı': 'applied there too',
  'bu ad orada başka bir dahiliyle kayıtlı': 'that name is registered there with another extension',
  'KOD orada başka bir temsilcide': 'the KOD belongs to another agent there',
  'bu DARK şifresi orada başka bir kayıtta': 'that DARK password is on another entry there',
  "oradaki kayıt başka bir KOD'da": 'the entry there is on another KOD',
  'şifre kaydı okunamadı': 'the password record could not be read',
  'proxy onaylamadı': 'the proxy did not confirm',
  'proxy KOD bildirmedi': 'the proxy reported no KOD',
  'kayıt değişmiş, tekrar deneyin': 'the entry changed, try again',

  // sms — nav + görünüm + kartlar (tablo sütun başlıkları panelin geri kalanı gibi TR kalır)
  'Gönderilen SMS kayıtları ve istatistikleri': 'Sent SMS records and statistics',
  İstatistik: 'Statistics',
  Kayıtlar: 'Records',
  'TOPLAM GÖNDERİM': 'TOTAL SENDS',
  GÖNDERİLDİ: 'SENT',
  BAŞARISIZ: 'FAILED',
  BAŞARI: 'SUCCESS',
  'Şablon dağılımı': 'Template breakdown',
  'Gönderim şablonuna göre': 'By message template',
  'Başarısızlık sebepleri': 'Failure reasons',
  'Başarısız gönderimlerin dağılımı': 'Breakdown of failed sends',
  'Başarısız gönderim yok': 'No failed sends',
  'Temsilci başına': 'Per agent',
  'Tüm sonuçlar': 'All results',
  'Tüm şablonlar': 'All templates',
  'Gönderim günlüğü': 'Send log',
  'SMS kaydı yok': 'No SMS records',
  'Seçili dönem ve markalar için SMS kaydı bulunamadı.': 'No SMS records for the selected period and brands.',
  // sms — temsilci detayı kartı
  'SMS günlüğü': 'SMS log',
  'Bu gün SMS gönderilmedi': 'No SMS sent this day',
  // sms — sonuç + şablon + sebep etiketleri (bizim sabit enum'larımız; değer olsa da çevrilir)
  Gönderildi: 'Sent',
  Başarısız: 'Failed',
  'Güncel Link': 'Latest Link',
  'Yatırım İptal': 'Failed Deposit',
  Şablonsuz: 'No template',
  Reddedildi: 'Rejected',
  'Gönderim kapalı': 'Send disabled',
  Yapılandırılmadı: 'Not configured',
  'Geçersiz numara': 'Invalid number',
  'Boş mesaj': 'Empty message',
  'Mesaj çok uzun': 'Message too long',

  // whatsapp — nav + kartlar + kayıt tablosu (sonuç/sebep/kanal DEĞERLERİ sheet'in birebir
  // metnidir ve çevrilmez; 'Yanıtladı' vb. zaten yukarıdaki durum sözlüğünden çevrilir)
  'WhatsApp / Telegram görüşme kayıtları ve istatistikleri': 'WhatsApp / Telegram conversation records and statistics',
  'TOPLAM GÖRÜŞME': 'TOTAL CONVERSATIONS',
  'YANIT ORANI': 'ANSWER RATE',
  'Sonuç dağılımı': 'Result breakdown',
  'Görüşme sonucuna göre': 'By conversation result',
  'Kanal dağılımı': 'Channel breakdown',
  'Görüşme sebepleri': 'Conversation reasons',
  "Sheet'teki sebep sütunu, birebir": 'The sheet reason column, verbatim',
  'Günlere göre': 'By day',
  'Gün başına görüşme sayısı': 'Conversations per day',
  ORAN: 'RATE',
  TARİH: 'DATE',
  MARKA: 'BRAND',
  KANAL: 'CHANNEL',
  NOT: 'NOTE',
  'Tüm kanallar': 'All channels',
  'Görüşme kayıtları': 'Conversation records',
  'WhatsApp kaydı yok': 'No WhatsApp records',
  'Seçili dönem ve markalar için WhatsApp kaydı bulunamadı.': 'No WhatsApp records for the selected period and brands.',
  'WhatsApp dosyasında bu markanın sekmesi yok': 'The WhatsApp workbook has no tab for this brand',
  'WhatsApp dosyası sunucuda yapılandırılmamış': 'The WhatsApp workbook is not configured on the server',
  'Google Sheets kotası doldu — birazdan tekrar deneyin': 'Google Sheets quota exhausted — try again shortly',
  'Dosya okuma sırasında değişti — tekrar deneyin': 'The workbook changed mid-read — try again',
  'WhatsApp dosyası okunamadı': 'The WhatsApp workbook could not be read',

  // winback — nav + sorgu + kartlar (bonus/oyun/sağlayıcı ADLARI BackOffice'in birebir metnidir,
  // çevrilmez; ay başlığı Intl ile dil bazlı üretilir, takvim günü satırı translatePhrase regex'i)
  'Müşteri ID ile yatırım, bonus ve oyun analizi': 'Deposit, bonus and game analysis by customer ID',
  'BackOffice müşteri ID': 'BackOffice customer ID',
  AY: 'MONTH',
  'Analiz Et': 'Analyze',
  'Analiz ediliyor…': 'Analyzing…',
  'Müşteri analizi': 'Customer analysis',
  "BackOffice müşteri ID'sini yazıp Analiz Et'e basın. Kayıt tarihi, son giriş, son 10 yatırım / çekim / oyun işlemi / kupon ve seçili dönem (bir ay ya da tüm zamanlar) için yatırım / bonus / oyun kırılımı gelir.":
    'Type the BackOffice customer ID and press Analyze. You get the registration date, last login, the last 10 deposits / withdrawals / game transactions / coupons and, for the selected period (one month or all time), the deposit / bonus / game breakdown.',
  // winback görev #168 — dönem seçici, son 10 çekim / oyun işlemi / kupon, `/winback` ayrı araç kabuğu
  Ay: 'Month',
  'Tüm zamanlar': 'All time',
  'Tüm zamanlar taraması biraz sürebilir': 'The all-time scan can take a while',
  'Ayrı sekmede aç': 'Open in own tab',
  'Call Agent paneli': 'Call Agent panel',
  'Erişim yok': 'No access',
  'Son çekimler': 'Last withdrawals',
  'Son oyun işlemleri': 'Last game transactions',
  'Son kuponlar': 'Last coupons',
  'Son 10 çekim': 'Last 10 withdrawals',
  'Taranan dönemde çekim yok': 'No withdrawals in the scanned period',
  'Son 10 oyun işlemi': 'Last 10 game transactions',
  'Taranan dönemde oyun işlemi yok': 'No game transactions in the scanned period',
  'Oyun işlemleri sayfa sınırına ulaştı — liste eksik olabilir': 'Game transactions hit the page cap — the list may be incomplete',
  'Son 10 kupon': 'Last 10 coupons',
  'Taranan dönemde kupon yok': 'No coupons in the scanned period',
  'Kupon listesi sayfa sınırına ulaştı — daha eski kuponlar eksik olabilir': 'The coupon list hit the page cap — older coupons may be missing',
  Bahis: 'Bet',
  Kazanç: 'Win',
  KUPON: 'COUPON',
  KATSAYI: 'ODDS',
  'OLASI KAZANÇ': 'POSSIBLE WIN',
  AÇIK: 'OPEN',
  // kupon durumları — BackOffice'in kendi sağlayıcı enum'larından (engine/winback.js COUPON_STATUS)
  KAZANDI: 'WON',
  KAYBETTİ: 'LOST',
  İADE: 'RETURNED',
  İŞLEMDE: 'PROCESSING',
  REDDEDİLDİ: 'DENIED',
  'PARA İADESİ': 'MONEY BACK',
  'GÜNÜN KOMBİNESİ': 'BET OF THE DAY',
  'YENİDEN SONUÇLANDI': 'RESETTLED',
  'İPTAL (KAZANÇ)': 'ROLLBACK (WIN)',
  'İPTAL (KAYIP)': 'ROLLBACK (LOSS)',
  'YATIRIM ADEDİ': 'DEPOSIT COUNT',
  'Aylara göre': 'By month',
  'Yalnız yatırım olan aylar': 'Only months with a deposit',
  // winback aylık kırılım (sahip 2026-10-07) — dönem seçici kalktı, tüm zamanların altında her ay
  'Aylık kırılım': 'Monthly breakdown',
  'Her ay ayrı — satıra tıklayınca o ayın kartları açılır': "Each month on its own — click a row to open that month's cards",
  'Tümünü aç': 'Expand all',
  'Tümünü kapat': 'Collapse all',
  'Bu dönemde ay yok': 'No months in this period',
  'EN ÇOK OYNANAN': 'MOST PLAYED',
  'Hareket yok': 'No activity',
  'Oyun kayıtları bu ay için kısmi — sayfa sınırı bu ayın içinde doldu': 'Game records for this month are partial — the page cap ran out inside this month',
  'Bu ay oyun kayıtları okunmadı — sayfa sınırı daha yeni aylarda doldu': 'Game records for this month were not read — the page cap ran out in newer months',
  "BackOffice müşteri ID'sini yazıp Analiz Et'e basın. Kayıt tarihi, son giriş, son 10 yatırım / çekim / oyun işlemi / kupon, tüm zamanlar için yatırım / bonus / oyun kırılımı ve altında her ayın ayrı kırılımı gelir.":
    'Type the BackOffice customer ID and press Analyze. You get the registration date, last login, the last 10 deposits / withdrawals / game transactions / coupons, the all-time deposit / bonus / game breakdown and, below it, each month on its own.',
  // ayrı Winback sitesi (web/src/winback-site/) — Ayarlar: şifre + kullanıcılar; sunucu hata metinleri
  'Şifremi değiştir': 'Change my password',
  'Mevcut şifreniz gerekir': 'Your current password is required',
  'MEVCUT ŞİFRE': 'CURRENT PASSWORD',
  'YENİ ŞİFRE (TEKRAR)': 'NEW PASSWORD (AGAIN)',
  'Şifreyi değiştir': 'Change password',
  'Yeni şifreler eşleşmiyor': 'The new passwords do not match',
  'Şifreniz değişti. Diğer cihazlardaki oturumlar kapatıldı.': 'Your password changed. Sessions on other devices were signed out.',
  'İşlem başarısız oldu.': 'The operation failed.',
  'GÖRÜNEN AD': 'DISPLAY NAME',
  'ör. asya': 'e.g. asya',
  'isteğe bağlı': 'optional',
  Kullanıcı: 'User',
  'Kullanıcı ekle': 'Add user',
  SİZ: 'YOU',
  Pasif: 'Inactive',
  'Yeni şifre': 'New password',
  'Sil': 'Delete',
  'Silmeyi onayla': 'Confirm delete',
  'Bu sitenin hesapları — Call Agent panelinden ayrıdır': "This site's accounts — separate from the Call Agent panel",
  'Kullanıcı adı 2–32 karakter olmalı: harf, rakam, . _ -': 'Username must be 2–32 characters: letters, digits, . _ -',
  'Mevcut şifre hatalı': 'Current password is incorrect',
  'Kendi hesabınızı silemezsiniz': 'You cannot delete your own account',
  'Kendi hesabınızı pasifleştiremezsiniz': 'You cannot deactivate your own account',
  'Kendi yönetici yetkinizi kaldıramazsınız': 'You cannot remove your own admin role',
  'En az bir aktif yönetici kalmalı': 'At least one active admin must remain',
  'Kullanıcı bulunamadı': 'User not found',
  'Geçersiz rol': 'Invalid role',
  'Görünen ad boş olamaz': 'Display name cannot be empty',
  'Bu dönemde yatırım yok': 'No deposits in this period',
  'Yatırım bonusları + freespinler, tüm zamanlar': 'Deposit bonuses + freespins, all time',
  'Bu dönemde bonus kullanılmamış': 'No bonus used in this period',
  'Bahis turu sayısına göre, tüm zamanlar (freespin turları hariç)': 'By number of bet rounds, all time (freespin rounds excluded)',
  'Bu dönemde oyun oynanmamış': 'No games played in this period',
  'Müşteri bulunamadı': 'Customer not found',
  'Bu sayfa için yetkiniz yok.': 'You are not allowed to use this page.',
  'Bu marka kapsamınızda değil.': 'This brand is outside your scope.',
  'Bu marka için BackOffice yapılandırılmamış.': 'BackOffice is not configured for this brand.',
  'Müşteri ID sayı olmalı.': 'The customer ID must be a number.',
  'Ay seçimi geçersiz.': 'Invalid month.',
  'Gelecek bir ay seçilemez.': 'A future month cannot be selected.',
  'Müşteri bilgisi': 'Customer info',
  'Ay içi yatırımlar': 'Deposits in month',
  'Son yatırımlar': 'Last deposits',
  Bonuslar: 'Bonuses',
  Freespinler: 'Freespins',
  Oyunlar: 'Games',
  'BackOffice hesabının bu uç için yetkisi yok': 'The BackOffice account lacks permission for this endpoint',
  'Bu marka için BackOffice yapılandırılmamış': 'BackOffice is not configured for this brand',
  'BackOffice istekleri sınırladı — birazdan tekrar deneyin': 'BackOffice throttled the requests — try again shortly',
  Okunamadı: 'Could not be read',
  'KAYIT TARİHİ': 'REGISTERED',
  'SON GİRİŞ': 'LAST LOGIN',
  BAKİYE: 'BALANCE',
  'TOPLAM YATIRIM': 'TOTAL DEPOSIT',
  'TOPLAM ÇEKİM': 'TOTAL WITHDRAWAL',
  'Son 10 yatırım': 'Last 10 deposits',
  'Taranan dönemde yatırım yok': 'No deposits in the scanned period',
  YÖNTEM: 'METHOD',
  TUTAR: 'AMOUNT',
  'AY İÇİ YATIRIM ADEDİ': 'DEPOSITS IN MONTH',
  'AY İÇİ YATIRIM TUTARI': 'DEPOSIT TOTAL IN MONTH',
  'YATIRIM YAPILAN GÜN': 'DAYS WITH DEPOSITS',
  'ORTALAMA YATIRIM': 'AVERAGE DEPOSIT',
  'Haftanın günlerine göre': 'By weekday',
  'Hangi gün daha çok yatırım yapıyor': 'Which day the customer deposits most',
  'Ayın haftalarına göre': 'By week of month',
  'AYIN GÜNLERİ': 'DAYS OF MONTH',
  '1–7. günler': 'Days 1–7',
  '8–15. günler': 'Days 8–15',
  '16–23. günler': 'Days 16–23',
  '24–31. günler': 'Days 24–31',
  'Günün saatlerine göre': 'By time of day',
  'SAAT ARALIĞI': 'TIME RANGE',
  'Takvim günlerine göre': 'By calendar day',
  'Yalnız yatırım olan günler': 'Only days with a deposit',
  'Bu ay yatırım yok': 'No deposits this month',
  'Kullanılan bonuslar': 'Bonuses used',
  'Yatırım bonusları + freespinler, bu ay': 'Deposit bonuses + freespins, this month',
  'Bu ay bonus kullanılmamış': 'No bonus used this month',
  'Yatırım bonusları okunamadı — yalnız freespinler listeleniyor': 'Deposit bonuses could not be read — only freespins are listed',
  'Freespinler okunamadı — yalnız yatırım bonusları listeleniyor': 'Freespins could not be read — only deposit bonuses are listed',
  BONUS: 'BONUS',
  TÜR: 'TYPE',
  'TUTAR / SPİN': 'AMOUNT / SPINS',
  İPTAL: 'CANCELLED',
  Freespin: 'Freespin',
  'Yatırım bonusu': 'Deposit bonus',
  'En çok oynanan 10 oyun': 'Top 10 games',
  'Bahis turu sayısına göre, bu ay (freespin turları hariç)': 'By number of bet rounds, this month (freespin rounds excluded)',
  'Oyun adı BackOffice yanıtında yok — sıralama yapılamadı': 'The BackOffice response carries no game name — no ranking possible',
  'Bu ay oyun oynanmamış': 'No games played this month',
  OYUN: 'GAME',
  SAĞLAYICI: 'PROVIDER',
  TUR: 'ROUNDS',
  BAHİS: 'STAKE',
  KAZANÇ: 'WIN',
  Pazartesi: 'Monday',
  Salı: 'Tuesday',
  Çarşamba: 'Wednesday',
  Perşembe: 'Thursday',
  Cuma: 'Friday',
  Cumartesi: 'Saturday',
  Pazar: 'Sunday',

  // Eklenti araç takımı (CLAUDE.md §30): Canlı Durum çipi/ipucu, Eklenti Sağlığı çekmecesi, toplu
  // işlem çubuğu, Eklenti filo sayfası. Sayı/ad taşıyan cümleler ext-health.ts L(lang,…) ile İKİ
  // dilde üretilir, burada yalnız sabit etiketler. `Durum`/`Günlük`/`Yükleniyor…`/`Uyarılar`
  // anahtarları başka sayfalarda başka anlama çevriliyor — bu yüzden çekmece Sağlık/Günlükler/
  // Okunuyor…/Yalnız uyarılar diyor.
  'Seçim modu': 'Select mode',
  BAKILMALI: 'NEEDS A LOOK',
  'Eklenti Sağlığını açmak için tıklayın': 'Click to open Extension Health',
  'Hızlı düzelt': 'Quick fix',
  Kapat: 'Close', // title=""
  'Komutlar ulaşamaz. Temsilciyi telefonla arayın.': 'Commands can’t reach it. Call the agent.',
  'Hızlı düzelt · temiz': 'Quick fix · cleared',
  'Hızlı düzelt · merdiven yürütülüyor': 'Quick fix · walking the ladder',
  'Önerir, siz tıklarsınız': 'Suggests, you click',
  'Önerilen sonraki adım': 'Suggested next step',
  Çalıştır: 'Run',
  Sağlık: 'Health',
  Hatalar: 'Errors',
  Günlükler: 'Logs',
  'Zaman çizelgesi': 'Timeline',
  İşlemler: 'Actions',
  'Sorun yok': 'OK',
  İzle: 'Watch',
  'Hemen düzelt': 'Fix now',
  Ölçülmedi: 'Not measured',
  'Yalnız hatalar': 'Errors only',
  'Yalnız uyarılar': 'Warnings only',
  'Onarım merdiveni': 'Repair ladder',
  'Kademe 1 tek tık · kademe 2 onay · kademe 3 adını yazın': 'Tier 1 one click · tier 2 confirm · tier 3 type the name',
  'Bağlantı testi': 'Ping',
  'Panel ulaşılabilir mi? Hiçbir şeyi değiştirmez.': 'Is the panel reachable? Changes nothing.',
  'Durum raporu al': 'Run diagnostics',
  'Durum listesini yeniler. Salt okunur.': 'Refreshes the Health checklist. Read-only.',
  'Bekleyen sonuçları gönder': 'Send pending results',
  '"Kuyrukta" takılan sonuçları iter.': 'Pushes results stuck in “queued”.',
  'Panel önbelleğini temizle': 'Clear panel cache',
  'Mola durumuna ve bekleyen sonuçlara dokunmaz.': 'Break state and pending results untouched.',
  'Yumuşak onarım': 'Soft repair',
  'Paneli yerinde yeniden başlatır, sayfa yenilenmez. Temsilci sonuç giriyorsa reddedilir.':
    'Restarts the panel in place, no page reload. Refused while a result is being entered.',
  'Sayfayı yenile': 'Reload page',
  'Sayfayı tamamen yeniler. Temsilci sonuç giriyorsa reddedilir.': 'Full page reload. Refused while a result is being entered.',
  'Paneli tamamen sıfırla': 'Full panel reset',
  'Temsilcinin adı yeniden sorulur; bekleyen sonuçlar korunur.': 'Re-asks the agent’s name; keeps pending results.',
  'Tüm komutlar (mesaj, sayfa, mola, kuyruk…)': 'All commands (message, sheet, break, queue…)',
  'Komut geçmişi': 'Command history',
  'Komutu gönder': 'Send command',
  'Temsilci sonuç giriyor — birazdan tekrar deneyin': 'Agent is entering a result — try again shortly',
  'Tekrar dene': 'Retry',
  Kopyalandı: 'Copied',
  'Yalnız uyarı ve hata satırları · panel kapanınca halka silinir': 'Warn and error lines only · cleared when the panel closes',
  'Rakam dizileri temsilcinin tarayıcısından çıkmadan ##### olarak maskelenir.': 'Digit runs are masked as ##### before they leave the agent’s browser.',
  'Bugün · panel açılışları, sürüm, uyarılar ve komutlar': 'Today · panel opens, versions, alerts and commands',
  'Aktif sayfa': 'Active sheet',
  Dil: 'Language',
  Duraklatıldı: 'Paused',
  Sıra: 'Queue',
  'Bugün biten': 'Done today',
  'Panel önbelleği': 'Panel cache',
  'SMS şablonları': 'SMS templates',
  'Atlanan satır': 'Skipped row',
  'Bekleyen kuyruk': 'Pending queue',
  'Sayfa geçişi (SPA)': 'Page navigation (SPA)',
  Bellek: 'Memory',
  'Sekme görünür': 'Tab visible',
  'Okunuyor…': 'Loading…',
  'Önbelleği temizle': 'Clear cache',
  'Mesaj gönder': 'Send message',
  Gönder: 'Send',
  'Sıfırlamalar yalnız tek temsilciye': 'Resets are single-agent only',
  'Temsilcilerin panelinde görünecek mesaj': 'Message shown on the agents’ panels', // placeholder
  Eklenti: 'Extension',
  'Call Agent eklentisi: sürümler ve dikkat isteyen paneller': 'Call Agent extension: versions and panels that need attention',
  "Chrome güncellemeyi birkaç saat içinde kendisi yapar; hemen almak için temsilci Chrome'u yeniden başlatmalı.":
    'Chrome applies updates within a few hours; to get one now the agent restarts Chrome.',
  'Sürüm dağılımı': 'Version distribution',
  'EN YENİ': 'NEWEST',
  GERİDE: 'BEHIND',
  'Şimdi bakılmalı': 'Needs attention now',
  "Her heartbeat ile güncellenir · sayaçlar bugün 00:00'dan beri": 'Updated with every heartbeat · counters since 00:00 today',
  'Şu an bakılacak bir şey yok': 'Nothing needs attention right now',
  Aç: 'Open',
  'Eski sürümdeki temsilciler': 'Outdated agents',
  'Tümünü göster': 'Show all',
  'Herkes en yeni sürümde': 'Everyone is on the newest version',
  SÜRÜM: 'VERSION',
  SAĞLIK: 'HEALTH',
  // Faz 2 (eklenti 2.3.0, migration 030).
  '7 gün': '7 days',
  'Fabrika ayarlarına döndür': 'Factory reset',
  'Mağazada güncelleme arar; varsa temsilci boştayken eklentiyi ve sayfayı yeniden yükler.':
    'Store update check; if found, reloads the extension and page when the agent is idle.',
  'Bekleyen sonuçlar dışındaki tüm ayar ve önbellekleri siler; ad ve şifre yeniden sorulur.':
    'Wipes settings and caches except pending results; name and password are asked again.',
  'Parmak izine göre gruplu · 30 gün saklanır': 'Grouped by fingerprint · kept 30 days',
  'Bir sonraki açılışta uygula': 'Apply on next start',
  YENİ: 'NEW',
  'Son 24 saatte satır yok.': 'No lines in the last 24 h.',
  Devam: 'Resume',
  Duraklat: 'Pause',
  'Hata ayıklama modu · 15 dk': 'Debug mode · 15 min',
  Kurulum: 'Install',
  Tarayıcı: 'Browser',
  'İşletim sistemi': 'Operating system',
  'Hata ayıklama': 'Debug logging',
  'Durum raporu': 'Diagnostics',
  "Günlük saatleri UTC'dir. Rakam dizileri ##### olarak maskelenir.": 'Log times are UTC. Digit runs are masked as #####.',
  'Eklentiyi güncelle': 'Update extension',
  Güncelle: 'Update',
  'En çok görülen hatalar': 'Top errors · fleet',
  'Bugün · 7 günlük eğilim · 2.3.0 ve sonrası gönderir': 'Today · 7-day trend · sent by 2.3.0 and later',
  'Bugün eklentiden hata gelmedi.': 'No extension errors today.',
  'Rapor kutusu': 'Reports inbox',
  'Eklentideki "Sorun bildir" ve anlık görüntüler · durum raporu otomatik eklenir':
    'From “Sorun bildir” in the extension and snapshots · diagnostics attached automatically',
  'Henüz rapor yok.': 'No reports yet.',
};

const PANEL_STEP_PREFIX_EN: Record<string, string> = {
  'hesabı var, şifre eşitlenemedi': 'has an account, password could not be matched',
  'hesap açılamadı': 'account could not be opened',
  'hesabı kaldırılamadı': 'account could not be removed',
  'uygulanamadı': 'could not apply',
  'kontrol edilemedi': 'could not check',
};

/** Panel hesabı adım metninin (marka öneki olmadan) EN karşılığı; tanınmıyorsa null. */
function panelStepEN(t: string): string | null {
  if (EN[t] != null) return EN[t];
  let m: RegExpMatchArray | null;
  if ((m = t.match(/^hesabı var \(giriş adı (.+)\)$/))) return 'has an account (sign-in name ' + m[1] + ')';
  if ((m = t.match(/^"(.+)" adı orada başka bir dahilide \(KOD (\d+)\) — önce onu düzeltin$/))) {
    return 'the name "' + m[1] + '" belongs to another extension there (KOD ' + m[2] + ') — fix that first';
  }
  if ((m = t.match(/^hesabı taşındı \(kaynak: (.+)\) — giriş adı (.+), (şifresi aynı|varsayılan şifre \(AD\.123\))$/))) {
    return 'account moved (from ' + m[1] + ') — sign-in name ' + m[2] + ', ' + (m[3] === 'şifresi aynı' ? 'same password' : 'default password (NAME.123)');
  }
  if ((m = t.match(/^kontrol edilemedi \((.+)\) — orada hesabı kalmış olabilir, tekrar deneyin$/))) {
    return 'could not check (' + (panelStepEN(m[1]) ?? m[1]) + ') — the account may still be there, try again';
  }
  if ((m = t.match(/^(hesabı var, şifre eşitlenemedi|hesap açılamadı|hesabı kaldırılamadı|uygulanamadı|kontrol edilemedi) — (.+)$/))) {
    return PANEL_STEP_PREFIX_EN[m[1]] + ' — ' + (panelStepEN(m[2]) ?? m[2]);
  }
  if ((m = t.match(/^(.+) hesabı okunamadı — (.+)$/))) return m[1] + ' account could not be read — ' + (panelStepEN(m[2]) ?? m[2]);
  if ((m = t.match(/^KOD orada başka bir temsilcide \((.+)\)$/))) return 'the KOD belongs to another agent there (' + m[1] + ')';
  if ((m = t.match(/^"(.+)" adı burada başka bir temsilci kaydına bağlı — Ayarlar'da birleştirin$/))) {
    return 'the name "' + m[1] + '" is linked to another agent record here — merge them in Settings';
  }
  return null;
}

/**
 * Bir tam dizgiyi çevirir. Sözlükte yoksa sayı taşıyan kalıpları dener
 * ("12 dk önce" → "12 min ago"). Eşleşme yoksa null → düğüme dokunulmaz.
 *
 * DİKKAT: çeviri METİN DÜĞÜMÜ başınadır. JSX'te `{x} dk` gibi serpiştirilmiş
 * yazım "53" ve " dk" diye AYRI düğümler üretir ve buradaki kalıplar asla
 * eşleşmez — dinamik TR dizgiyi TEK ifade olarak birleştir: `{x + ' dk'}`.
 */
export function translatePhrase(x: string): string | null {
  if (EN[x] != null) return EN[x];
  let m: RegExpMatchArray | null;
  // Panel hesabı adımları (src/api/panel-accounts.js): "<Marka>: <metin>" — marka adı aynen kalır.
  if ((m = x.match(/^(Vegas|Prime VIP|Prime|Hera|BetBooker): (.+)$/))) {
    const t = panelStepEN(m[2]);
    if (t != null) return m[1] + ': ' + t;
  }
  if ((m = x.match(/^Diğer markalara taşınamadı — (.+)$/))) return 'Could not carry it to the other brands — ' + (panelStepEN(m[1]) ?? m[1]);
  // Winback: sayı taşıyan kart sayaçları + takvim günü satırı ('07.09 Pazartesi' tarih + gün adı TEK düğüm).
  if ((m = x.match(/^(\d+) bonus$/))) return m[1] + ' bonuses';
  if ((m = x.match(/^(\d+) oyun$/))) return m[1] + ' games';
  if ((m = x.match(/^(\d+) spin$/))) return m[1] + ' spins';
  if ((m = x.match(/^(\d{2}\.\d{2}) (Pazartesi|Salı|Çarşamba|Perşembe|Cuma|Cumartesi|Pazar)$/))) return m[1] + ' ' + EN[m[2]];
  // Winback son 10 kart başlıkları + tüm zamanlar sınırı + oyun kesilme notu (tarih/sayı taşır).
  if ((m = x.match(/^Tarama başlangıcı (.+)$/))) return 'Scanned from ' + m[1];
  if ((m = x.match(/^Son: (.+)$/))) return 'Latest: ' + m[1];
  if ((m = x.match(/^Son (\d+) ay ile sınırlı$/))) return 'Limited to the last ' + m[1] + ' months';
  if ((m = x.match(/^Marka saati \((.+)\)$/))) return 'Brand time (' + m[1] + ')';
  if ((m = x.match(/^Oyun kayıtları (\d+) satırda kesildi — sıralama yalnız okunan kısmı yansıtır$/))) return 'Game records cut at ' + m[1] + ' rows — the ranking reflects only what was read';
  if ((m = x.match(/^Oyun kayıtları (\d+) satırda kesildi — sıralama (.+) sonrasını kapsıyor$/))) return 'Game records cut at ' + m[1] + ' rows — the ranking covers ' + m[2] + ' onwards';
  if ((m = x.match(/^(\d+) \/ (\d+) temsilci$/))) return m[1] + ' / ' + m[2] + ' agents';
  if ((m = x.match(/^(\d+) temsilci$/))) return m[1] + ' agents';
  // Bütün Aramalar (görev #193/#194) — sayı / ID / süre taşıyan dizgiler.
  if ((m = x.match(/^Sonucu kaydedilmeyen: (\d+)$/))) return 'No outcome saved: ' + m[1];
  if ((m = x.match(/^En yeni (\d+) çağrı gösteriliyor; daha eskileri var\.$/))) return 'Showing the newest ' + m[1] + ' calls; older ones exist.';
  if ((m = x.match(/^Aramalar alınamadı — (.*)$/))) return 'Calls could not be loaded — ' + m[1];
  if ((m = x.match(/^(.+) markasında (.+) numaralı müşteriye kayıtlı çağrı yok\.$/))) return 'No calls recorded to customer ' + m[2] + ' on ' + m[1] + '.';
  if ((m = x.match(/^Bağlanmadı · çalma ([\d:]+)$/))) return 'Not connected · rang ' + m[1];
  // Haftalık Rapor otomatik SMS kapsamı (görev #189): veri hafta ortasında başladıysa.
  if ((m = x.match(/^(\d{2}\.\d{2})'den beri$/))) return 'since ' + m[1];
  if ((m = x.match(/^Otomatik SMS (\d{2}\.\d{2})'den beri$/))) return 'Automatic SMS since ' + m[1];
  // Liste Kırılımı BAŞARI hücresinin title'ı (görev #202) — fmtMoney binlik noktası taşıyabilir.
  if ((m = x.match(/^([\d.]+) yatıran \/ ([\d.]+) görüşme$/))) return m[1] + ' depositors / ' + m[2] + ' conversations';
  // Temsilci Karnesi BAŞARI ORANI title'ı (görev #297): payda DAVET'siz görüşme.
  if ((m = x.match(/^([\d.]+) yatıran \/ ([\d.]+) görüşme \(DAVET hariç\)$/))) return m[1] + ' depositors / ' + m[2] + ' conversations (DAVET excluded)';
  // QMS hücresinin title'ı: sayı taşıdığı için sözlüğe giremez.
  if ((m = x.match(/^(\d+) değerlendirme$/))) return m[1] + ' evaluations';
  // Haftanın Temsilcisi KRİTİK CEZA hücresinin title'ı (görev #274/#275).
  if ((m = x.match(/^(\d+) kritik hata$/))) return m[1] + ' critical mistakes';
  if ((m = x.match(/^(\d+) kayıt$/))) return m[1] + ' records';
  if ((m = x.match(/^(\d+) seçili · (\d+) yeni$/))) return m[1] + ' selected · ' + m[2] + ' new';
  if ((m = x.match(/^(\d+) yeni$/))) return m[1] + ' new';
  if ((m = x.match(/^(\d+) dk önce$/))) return m[1] + ' min ago';
  if ((m = x.match(/^(\d+) sa önce$/))) return m[1] + ' h ago';
  if ((m = x.match(/^(\d+) gün önce$/))) return m[1] + ' d ago';
  if (x === 'az önce') return 'just now';
  if ((m = x.match(/^(\d+) dk hak kaldı$/))) return m[1] + ' min left';
  // Hata şeritleri tek ifade olarak basılır ("… — <sunucu mesajı>"), bu yüzden ön ek regex'le eşleşir.
  if ((m = x.match(/^Mola verileri yüklenemedi — (.*)$/))) return 'Break data could not be loaded — ' + m[1];
  if ((m = x.match(/^Mola sonlandırılamadı — (.*)$/))) return 'Break could not be ended — ' + m[1];
  if ((m = x.match(/^Ayar kaydedilemedi — (.*)$/))) return 'Setting could not be saved — ' + m[1];
  if (x === 'Mola sonlandırılamadı') return 'Break could not be ended';
  if (x === 'Mola verileri yüklenemedi') return 'Break data could not be loaded';
  if ((m = x.match(/^(\d+) dk kaldı$/))) return m[1] + ' min left';
  if ((m = x.match(/^(\d+) dk boşta$/))) return m[1] + ' min idle';
  if ((m = x.match(/^Mola öncesi (\d+) dk boşta$/))) return m[1] + ' min idle before break';
  if ((m = x.match(/^Mola (\d{1,2}:\d{2})$/))) return 'Break ' + m[1];
  if ((m = x.match(/^(\d+) dk · (\d+)×$/))) return m[1] + ' min · ' + m[2] + '×';
  if ((m = x.match(/^\+(\d+) dk$/))) return '+' + m[1] + ' min';
  if ((m = x.match(/^(\d+) dk$/))) return m[1] + ' min';
  // DARK konuşma süresi / mola geçen süre (fmtTalkLong, fmtElapsed): "1sa 24dk" → "1h 24m", "24dk" → "24m"
  if ((m = x.match(/^(\d+)sa (\d+)dk$/))) return m[1] + 'h ' + m[2] + 'm';
  if ((m = x.match(/^(\d+)dk$/))) return m[1] + 'm';
  if ((m = x.match(/^Seçilenleri Onayla \((\d+)\)$/))) return 'Approve selected (' + m[1] + ')';
  // Onay açıklaması modalı: toplu gövde (sayı taşır → sözlüğe giremez). "N / 500" sayacı dilsiz.
  if ((m = x.match(/^(\d+) uyarı onaylanacak — aynı açıklama hepsine yazılır\. Açıklamasız onay kaydedilmez\.$/)))
    return m[1] + ' alerts will be approved — the same note is written to all of them. An approval without a note is not saved.';
  if ((m = x.match(/^Gün (.+) · (\d+) satır$/))) return 'Day ' + m[1] + ' · ' + m[2] + ' rows';
  // Haftalık Rapor "Veri uyarısı" satırları — sunucu üretir (engine/weekly-report.js buildBrandReport,
  // api/weekly-report.js withStaleCaveat + computeBrandReport), sayı/tarih/sekme adı taşır → sözlüğe
  // giremez, burada regex. Sunucu metnini değiştirirsen BURAYI da değiştir; eski metin L2 cache'te
  // tazelik penceresi (≤6 sa) boyunca yaşar, o yüzden eski kalıp da bir süre tutulur.
  if ((m = x.match(/^(\d+) yatırım hücresi hiç yazılmadı ve artık kendiliğinden dolmayacak \(vadesi geçti; ör\. D sütununda sayısal ID yerine kullanıcı adı ya da yazmaya kapalı sekme\) — bu haftanın toplamları eksik\.$/)))
    return m[1] + ' deposit cells were never written and will not fill on their own (overdue; e.g. a username instead of a numeric ID in column D, or a write-protected tab) — this week\'s totals are incomplete.';
  if ((m = x.match(/^(\d+) yatırım hücresi HENÜZ BOŞ \(depositcheckup yazmamış\) — toplamlar eksik olabilir\.$/)))
    return m[1] + ' deposit cells are still EMPTY (not yet written by depositcheckup) — totals may be incomplete.';
  if ((m = x.match(/^Bu sekmelerde 24\/48 saat sütunu bulunamadı \(başlık satırı değişmiş olabilir\): (.+)$/)))
    return 'No 24/48h column found on these tabs (the header row may have changed): ' + m[1];
  if ((m = x.match(/^Bu sekmeler dosyada bulunamadı: (.+)$/)))
    return 'These tabs were not found in the file: ' + m[1]
      .replace(/\(WhatsApp dosyası okunamadı — erişim yetkisi\)/g, '(WhatsApp file unreadable — access permission)')
      .replace(/\(WhatsApp dosyası yapılandırılmamış\)/g, '(WhatsApp file not configured)')
      .replace(/\(WhatsApp dosyası\)/g, '(WhatsApp file)');
  if ((m = x.match(/^Bu hafta ŞU AN yeniden okunamadı \((.+?)\) — ekrandaki sayılar (.+) tarihli son başarılı taramadan(?: · (\d+) (dk|sa) önce)?\.$/)))
    return 'This week could not be re-read RIGHT NOW (' + m[1] + ') — the numbers shown are from the last successful scan on '
      + m[2] + (m[3] ? ' · ' + m[3] + (m[4] === 'dk' ? ' min' : ' h') + ' ago' : '') + '.';
  if ((m = x.match(/^Bu hafta ŞU AN yeniden okunamadı \((.+?)\) — ekrandaki sayılar daha önceki son başarılı taramadan\.$/)))
    return 'This week could not be re-read RIGHT NOW (' + m[1] + ') — the numbers shown are from an earlier successful scan.';
  if ((m = x.match(/^(\d+) çağrı · (.+)$/))) return m[1] + ' calls · ' + m[2];
  // Eşik alıntılayan KURAL BAŞLIĞI: sayı config_kv.thresholds'ten gelir, sabit değil.
  // Hem uyarı satırının başlığı hem de Uyarılar'daki kural tipi filtresi bu metni basar.
  if ((m = x.match(/^Uzun boşluk \(>(\d+) dk\)$/))) return 'Long gap (>' + m[1] + ' min)';
  // backend'in ÜRETTİĞİ dinamik uyarı açıklamaları (engine/*, ingest/*, jobs/*)
  if ((m = x.match(/^(\d{1,2}:\d{2}) → (\d{1,2}:\d{2}) arası (\d+) dk boşluk$/)))
    return m[1] + ' → ' + m[2] + ' · ' + m[3] + ' min gap';
  if ((m = x.match(/^Günlük mola hakkı aşıldı \((\d+)\/(\d+) dk\)$/)))
    return 'Daily break allowance exceeded (' + m[1] + '/' + m[2] + ' min)';
  if ((m = x.match(/^(\d+) çağrı sonucu panele hiç ulaşmadı$/)))
    return m[1] + ' call results never reached the panel';
  if ((m = x.match(/^Son çağrıdan (\d+) dk sonra mola başlattı$/)))
    return 'Started break ' + m[1] + ' min after last call';
  if ((m = x.match(/^"(.+)" sekmesinde satır kayması tespit edildi — senkron donduruldu$/)))
    return 'Row shift detected in "' + m[1] + '" — sync frozen';
  if ((m = x.match(/^(.+) günü için DARK raporu yüklenmedi$/)))
    return 'DARK report not uploaded for ' + m[1];
  if ((m = x.match(/^Terminal (\d+) \/ DARK (\d+) — fark (\d+)$/)))
    return 'Terminal ' + m[1] + ' / DARK ' + m[2] + ' — diff ' + m[3];
  // dark_uyusmazligi arama ayağı açıklaması (jobs/alert-crons.js darkMismatchAlert).
  if ((m = x.match(/^Terminal (\d+) \/ Arama (\d+) \/ DARK (\d+) \(KOD (.+)\)$/)))
    return 'Terminal ' + m[1] + ' / Dials ' + m[2] + ' / DARK ' + m[3] + ' (code ' + m[4] + ')';
  // cagri_hedefi_alti / dusuk_yanit_orani açıklamaları (engine/daily-targets.js dailyTargetAlerts).
  if ((m = x.match(/^(\d+) çağrı — günlük hedef (\d+)$/)))
    return m[1] + ' calls — daily target ' + m[2];
  if ((m = x.match(/^Yanıtlanma oranı %(\d+),(\d) \((\d+)\/(\d+)\) — alt sınır %(\d+)$/)))
    return 'Answer rate ' + m[1] + '.' + m[2] + '% (' + m[3] + '/' + m[4] + ') — floor ' + m[5] + '%';
  // dahili_uyusmazligi açıklaması: " · " ile birleşmiş parçalar (jobs/alert-crons.js
  // extensionMismatchAlerts). Her parça tanınmalı, yoksa dokunulmaz.
  if (/^Kullanılan KOD /.test(x)) {
    const out = x.split(' · ').map((p) => {
      let mm: RegExpMatchArray | null;
      if ((mm = p.match(/^Kullanılan KOD (.+)$/))) return 'Code used ' + mm[1];
      if ((mm = p.match(/^Panel KOD (.+)$/))) return 'Panel code ' + mm[1];
      if ((mm = p.match(/^Kayıt KOD (.+)$/))) return 'Registry code ' + mm[1];
      if ((mm = p.match(/^Aynı KOD: (.+)$/))) return 'Same code: ' + mm[1];
      return null;
    });
    if (out.every((p) => p !== null)) return out.join(' · ');
  }
  // Softphone ölçümü title'ları (Canlı Durum ÇAĞRI, haftalık kart, Raporlar, Haftalık Rapor).
  if ((m = x.match(/^Arama (\d+) · bağlanan (\d+) · konuşma (.+)$/)))
    return 'Dials ' + m[1] + ' · connected ' + m[2] + ' · talk ' + (translatePhrase(m[3]) ?? m[3]);
  if ((m = x.match(/^Arama (\d+) · bağlanan (\d+) · ortalama (.+)$/)))
    return 'Dials ' + m[1] + ' · connected ' + m[2] + ' · average ' + m[3];
  if ((m = x.match(/^Arama (\d+) · bağlanan (\d+)$/))) return 'Dials ' + m[1] + ' · connected ' + m[2];
  if ((m = x.match(/^Toplam konuşma (.+)$/))) return 'Total talk ' + m[1];
  // Canlı Durum kartının eklenti sürümü çipi (title'ı sürüm taşır; nedenler ipucu kutusunda, §30).
  if ((m = x.match(/^Eklenti sürümü (\S+)$/))) return 'Extension version ' + m[1];
  if ((m = x.match(/^Karnede olmayan: (.+)$/))) return 'Not on the scorecard: ' + m[1];
  // Döngü süreleri ("1,8 sn").
  if ((m = x.match(/^(\d+),(\d) sn$/))) return m[1] + '.' + m[2] + ' s';
  // Çağrı günlüğü satırının ölçülen-çağrı title'ı: " · " ile birleşmiş "Etiket değer" parçaları.
  if (/^(Arandı|Cevaplandı|Bitti|Çalma|Kapatan|Sebep) /.test(x)) {
    const LBL: Record<string, string> = {
      Arandı: 'Dialled', Cevaplandı: 'Answered', Bitti: 'Ended', Çalma: 'Ringing', Kapatan: 'Hung up by', Sebep: 'Cause',
    };
    const WHO: Record<string, string> = { temsilci: 'agent', müşteri: 'customer', sistem: 'system' };
    const out = x.split(' · ').map((p) => {
      const mm = p.match(/^(Arandı|Cevaplandı|Bitti|Çalma|Kapatan|Sebep) (.+)$/);
      if (!mm) return null;
      return LBL[mm[1]] + ' ' + (mm[1] === 'Kapatan' ? WHO[mm[2]] || mm[2] : mm[2]);
    });
    if (out.every((p) => p !== null)) return out.join(' · ');
  }
  // saat_sapmasi payload değeri (ingest/dedup.js formatSkew): "60 dk geri" / "12 dk ileri".
  // Yönü kaybetme — "behind"/"ahead" hangi saatin yanlış olduğunu değil, NE YÖNDE
  // yanlış olduğunu söyler ve teşhisi o taşır.
  if ((m = x.match(/^(\d+) dk (geri|ileri)$/)))
    return m[1] + ' min ' + (m[2] === 'geri' ? 'behind' : 'ahead');
  // vardiya planı — rozetler, şeritler ve backend'in ürettiği açıklamalar
  if ((m = x.match(/^(\d{1,2}:\d{2})–(\d{1,2}:\d{2})$/))) return m[1] + '–' + m[2];
  if ((m = x.match(/^(Sabah|Akşam) vardiyası (\d{1,2}:\d{2})–(\d{1,2}:\d{2})$/)))
    return (m[1] === 'Sabah' ? 'Morning' : 'Evening') + ' shift ' + m[2] + '–' + m[3];
  // Aynı rozet, gelen çağrı görevinde: "Sabah gelen çağrı 10:00–18:00".
  if ((m = x.match(/^(Sabah|Akşam) gelen çağrı (\d{1,2}:\d{2})–(\d{1,2}:\d{2})$/)))
    return (m[1] === 'Sabah' ? 'Morning' : 'Evening') + ' inbound ' + m[2] + '–' + m[3];
  // Vegas/Prime sabit penceresi (plan yok): "Vardiya 10:30–18:30"
  if ((m = x.match(/^Vardiya (\d{1,2}:\d{2})–(\d{1,2}:\d{2})$/))) return 'Shift ' + m[1] + '–' + m[2];
  if ((m = x.match(/^Vardiya (\d{1,2}:\d{2}) başladı, bugün hiç aktivite yok$/)))
    return 'Shift started at ' + m[1] + ', no activity today';
  if ((m = x.match(/^Planda (İzinli|Takım lideri) olmasına rağmen (\d+) çağrı yapıldı$/)))
    return 'Rostered as ' + (m[1] === 'İzinli' ? 'day off' : 'team lead') + ' but made ' + m[2] + ' calls';
  if ((m = x.match(/^Vardiya planı sekmesi bulunamadı: (.+)$/)))
    return 'Shift roster tab not found: ' + m[1];
  if ((m = x.match(/^“(.+)” sekmesinden her gün (\d{1,2}:\d{2}) \(İstanbul\) okunur$/)))
    return 'Read daily at ' + m[2] + ' (Istanbul) from the “' + m[1] + '” tab';
  if ((m = x.match(/^Eşleşmeyen isim: (.+)$/))) return 'Unmatched names: ' + m[1];
  if ((m = x.match(/^Boş hücre: (\d+) \(satır üretmez\)$/))) return 'Blank cells: ' + m[1] + ' (no rows written)';
  if ((m = x.match(/^Çok sık yenileme — (\d+) sn sonra tekrar deneyin$/)))
    return 'Refreshing too often — try again in ' + m[1] + ' s';
  // ayarlar → kullanıcı ekleme/şifre sıfırlama; uzunluk sunucudaki MIN_PASSWORD_LEN'den gelir
  if ((m = x.match(/^Okunamayan sağlayıcı: (.+)$/))) return 'Unreadable provider: ' + m[1];
  // Winback sitesi Ayarlar bildirimleri (kullanıcı adı taşır).
  if ((m = x.match(/^(.+) için şifre belirlendi$/))) return 'Password set for ' + m[1];
  if ((m = x.match(/^(.+) rolü güncellendi$/))) return m[1] + ' role updated';
  if ((m = x.match(/^(.+) pasifleştirildi$/))) return m[1] + ' deactivated';
  if ((m = x.match(/^(.+) aktifleştirildi$/))) return m[1] + ' activated';
  if ((m = x.match(/^(.+) eklendi$/))) return m[1] + ' added';
  if ((m = x.match(/^(.+) silindi$/))) return m[1] + ' deleted';
  if ((m = x.match(/^en az (\d+) karakter$/))) return 'at least ' + m[1] + ' characters';
  if ((m = x.match(/^Şifre en az (\d+) karakter olmalı$/)))
    return 'Password must be at least ' + m[1] + ' characters';
  // ayarlar → panel şifreleri (src/api/panel-passwords.js)
  if ((m = x.match(/^Şifre (\d+)–(\d+) karakter olmalı$/))) return 'Password must be ' + m[1] + '–' + m[2] + ' characters';
  if ((m = x.match(/^Proxy hatası \((.+)\)$/))) return 'Proxy error (' + m[1] + ')';
  if ((m = x.match(/^row (\d+): second date-header block ignored \(rows (\d+)\+\)$/))) return x;
  // haftalık rapor — sayı taşıdığı için sözlüğe giremeyen üç dizge. Üçü de JSX'te
  // TEK ifade olarak kurulur (`{a + ' … ' + b}`), yoksa metin düğümü bölünür ve
  // hiçbir kalıp eşleşmezdi.
  if ((m = x.match(/^Haftalık rapor yüklenemedi — (.*)$/))) return 'Weekly report could not be loaded — ' + m[1];
  if ((m = x.match(/^Rapor hesaplanıyor — (\d+) sn sonra tekrar denenecek$/)))
    return 'Report is being computed — retrying in ' + m[1] + ' s';
  // Sayı tr-TR binlik ayıracıyla gelir ("1.510 mesaj"), o yüzden \d değil [\d.].
  if ((m = x.match(/^([\d.]+) mesaj$/))) return m[1] + ' messages';
  // sms — günlük kırpma notu (sayı taşır, TEK ifade olarak basılır).
  if ((m = x.match(/^En yeni (\d+) kayıt gösteriliyor — daraltmak için dönem veya süzgeç kullanın$/)))
    return 'Showing the newest ' + m[1] + ' records — narrow with the period or filters';
  // whatsapp — marka başına kaynak notu: "Vegas: <not> · Prime: <not>" TEK ifade olarak basılır;
  // marka adı kalır, her notun kendisi sözlükten çevrilir (hepsi çevrilemezse dokunulmaz).
  if (x.includes(': ') && /^(Vegas|Prime|Hera|BetBooker): /.test(x)) {
    const parts = x.split(' · ');
    const out = parts.map((p) => {
      const mm = p.match(/^(Vegas|Prime|Hera|BetBooker): (.+)$/);
      return mm && EN[mm[2]] != null ? mm[1] + ': ' + EN[mm[2]] : null;
    });
    if (out.every((p) => p !== null)) return out.join(' · ');
  }
  return null;
}

/** Kök altındaki metin düğümlerini, placeholder'ları ve title'ları çevirir. */
function translateDOM(lang: Lang, root: HTMLElement | null): void {
  if (lang !== 'en') return;
  const el = root || document.body;
  if (!el) return;

  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
    acceptNode: (t) => {
      const p = t.parentNode && t.parentNode.nodeName;
      return p === 'SCRIPT' || p === 'STYLE' ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes: Node[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) nodes.push(node);
  nodes.forEach((n) => {
    const raw = n.nodeValue;
    if (!raw) return;
    const k = raw.trim();
    if (!k) return;
    const e = translatePhrase(k);
    if (e != null && e !== k) n.nodeValue = raw.replace(k, e);
  });

  el.querySelectorAll('input[placeholder], textarea[placeholder]').forEach((input) => {
    const i = input as HTMLInputElement | HTMLTextAreaElement;
    const k = (i.placeholder || '').trim();
    const e = translatePhrase(k);
    if (e != null && e !== k) i.placeholder = e;
  });
  el.querySelectorAll('[title]').forEach((t) => {
    const k = (t.getAttribute('title') || '').trim();
    const e = translatePhrase(k);
    if (e != null && e !== k) t.setAttribute('title', e);
  });
}

/**
 * EN kaplamasını uygular. İKİ tetikleyici:
 *
 *  1. useLayoutEffect (bağımlılık dizisi YOK): her App commit'inden sonra, boyamadan
 *     ÖNCE çalışır — dil değişimi + kök yeniden montajını ve App-seviyesi render'ları yakalar.
 *  2. MutationObserver: App RE-RENDER OLMADAN alt sayfalarda ASENKRON gelen içeriği
 *     (fetch sonrası kartlar/tablolar, 30sn poll güncellemeleri, backend uyarı açıklamaları,
 *     modallar) yakalar. useLayoutEffect yalnız App yeniden render olunca çalışır; bir alt
 *     bileşen kendi state'iyle render olduğunda App render OLMAZ → o içerik çevrilmeden kalırdı.
 *     Bu, "her şey çevrilmiyor" hatasının köküydü.
 *
 * Kendi metin-düğümü değişikliklerimizin observer'ı yeniden tetiklemesini önlemek için
 * çeviri sırasında observer'ı disconnect edip sonra reconnect ediyoruz (sonsuz döngü yok).
 */
export function useTranslateDOM(lang: Lang, rootRef: React.RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    translateDOM(lang, rootRef.current);
  });

  useEffect(() => {
    if (lang !== 'en') return;
    const root = rootRef.current;
    if (!root || typeof MutationObserver === 'undefined') return;

    // SENKRON çeviri (rAF YOK): observer callback'i bir microtask'tır — DOM mutasyonundan
    // sonra, BOYAMADAN önce çalışır → görsel Türkçe "parlaması" olmaz. rAF kullanmıyoruz
    // çünkü sekme ön planda değilken rAF kısılır/durur ve zamanlayıcı-güdümlü güncellemeler
    // ("38 dk önce" dakika dönüşü) asla çevrilmezdi. disconnect de YOK: translateDOM idempotent
    // (EN düğümler hiçbir kalıba uymaz → değişiklik yok → yeni mutasyon yok → döngü kendiliğinden
    // durur), bu yüzden disconnect penceresinde mutasyon kaçırma riski de ortadan kalkar.
    const obs = new MutationObserver(() => translateDOM('en', root));
    obs.observe(root, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, [lang, rootRef]);
}
